import {getLocale, setHandleMissingTranslation, setLocale, setTranslations, translate} from "react-i18nify";

import * as autotaskTypes from "../constants/autotaskTypes";
import * as componentTypes from "../constants/componentTypes";
import * as statuses from "../constants/databaseStatuses";
import * as fileTypes from "../constants/fileTypes";
import * as formulaErrors from "../constants/formulaErrors";
import * as infoData from "../constants/formulaInfoData";
import * as helper from "../constants/helper";
import * as keyCodes from "../constants/keyCodes";
import * as keyWords from "../constants/keyWords";
import * as languages from "../constants/languages";
import * as modules from "../constants/modules";
import * as orderStatus from "../constants/orderStatus";
import * as priceTypes from "../constants/priceTypes";
import * as qualityControl from "../constants/qualityControl";
import * as resources from "../constants/resources";
import * as resultCodes from "../constants/resultCodes";
import * as rcc from "../constants/rcc";
import * as settings from "../constants/settings";
import * as setup from "../constants/setup";
import * as statsTypes from "../constants/statsTypes";
import * as ticketTypes from "../constants/ticketTypes";
import * as modelHelper from "../helpers/modelHelper";
import * as translationsProvider from "../lang/translations";
import * as windowService from "../services/windowService";

import keyWordsData from "../resources/data/keyWords.json";

import Language from "../models/utils/Language";

const validNumberFormat = Intl.NumberFormat;

function isSupportedLanguage(supportedLanguages, languageCode) {
    return supportedLanguages.some((lang) => languageCode === lang.code);
}

/**
 * Merges supported languages and custom languages.
 *
 * @returns {Array}
 */
export const getRedlikeLanguages = () => {
    let customLanguages = windowService.getCustomLanguages();
    let supportedLanguages = translationsProvider.getSupportedLanguages();

    let code;
    for (code in customLanguages) {
        if (!isSupportedLanguage(supportedLanguages, code)) {
            supportedLanguages.push(new Language(code, customLanguages[code], customLanguages[code], true));
        }
    }

    return supportedLanguages;
};

export const toCamelCase = (str) => {
    return str
        .replace(/(?:^\w|[A-Z]|\b\w)/g, function (letter, index) {
            return index === 0 ? letter.toLowerCase() : letter.toUpperCase();
        })
        .replace(/\s+/g, "");
};

function sortObjectKeys(translationObject) {
    if (typeof translationObject === "string") {
        return translationObject;
    }

    var objectKeys = Object.keys(translationObject);

    objectKeys.sort(function (a, b) {
        if (a < b) {
            return -1;
        }

        if (a > b) {
            return 1;
        }

        return 0;
    });

    var result = {};

    var key;
    for (key of objectKeys) {
        result[key] = translationObject[key];
    }

    return result;
}

function areParametersValid(key, defaultTranslation, translation) {
    if (defaultTranslation.match(/[%{}]/) === null) {
        return null;
    }

    let validParameters = defaultTranslation.match(/%{[a-zA-Z0-9]+}/g);

    if (validParameters === null) {
        return null;
    }

    let parameters = translation.match(/%{[a-zA-Z0-9]+}/g);

    if (parameters === null || validParameters.length !== parameters.length) {
        return (
            "Wrong number of parameters in translation '" +
            key +
            "':" +
            "\n\nOriginal: " +
            defaultTranslation +
            "\n\nCustom:   " +
            translation
        );
    }

    for (let i = 0; i < validParameters.length; i++) {
        if (validParameters[i] !== parameters[i]) {
            return (
                "Wrong name of parameter in translation '" +
                key +
                "':" +
                "\n\nOriginal - " +
                defaultTranslation +
                "\n\nCustom   - " +
                translation
            );
        }
    }

    return null;
}

function validateParams(english, key, translation) {
    if (typeof english[key] === "string") {
        if (translation && translation[key]) {
            return areParametersValid(key, english[key], translation[key]);
        }

        return null;
    }

    if (translation[key] === undefined) {
        return null;
    }

    let result = null;

    for (let k of Object.keys(english[key])) {
        result = validateParams(english[key], k, translation[key]);

        if (result !== null) {
            return result;
        }
    }

    return null;
}

function validateTranslationParams(defaultTranslation, translations) {
    let result = null;

    for (let key of Object.keys(defaultTranslation)) {
        result = validateParams(defaultTranslation, key, translations);

        if (result !== null) {
            return result;
        }
    }

    return null;
}

function getChildValues(english, key, translation) {
    if (typeof english[key] === "string") {
        if (translation && translation[key]) {
            return translation[key];
        }

        return english[key];
    }

    if (translation[key] === undefined) {
        return sortObjectKeys(english[key]);
    }

    let result = modelHelper.copy(english[key]);

    let k;
    for (k of Object.keys(english[key])) {
        result[k] = getChildValues(english[key], k, translation[key]);
    }

    return sortObjectKeys(result);
}

function mergeDefaultTranslations(defaultTranslation, translations) {
    let result = {};

    let key;
    for (key of Object.keys(defaultTranslation)) {
        result[key] = getChildValues(defaultTranslation, key, translations);
    }

    return sortObjectKeys(result);
}

/**
 * Exports translation keys to JSON format.
 *
 * @param   {String}    language
 * @returns {String}
 */
export const exportTranslationKeysToJson = (language) => {
    let englishTranslations = translationsProvider.getDefaultTranslations(languages.ENGLISH_GB);
    let translations = translationsProvider.getDefaultTranslations(language);

    return stringifyJson(mergeDefaultTranslations(englishTranslations, translations));
};

function getExportFileDescription(language) {
    let date = new Date();

    let description = '[Global]\nlast_update="';
    description += '"' + date.getFullYear() + "/" + (date.getMonth() + 1) + "/" + date.getDate() + '"\n';
    description += 'version="1"\nname="' + translate("languages." + language) + '"\napp="Redlike2"\n[Messages]\n';

    return description;
}

function getChildExport(language, parentKey, key, result) {
    if (typeof language[key] === "string") {
        result[parentKey] = language[key];

        return result;
    }

    let k;
    for (k of Object.keys(language[key])) {
        getChildExport(language[key], parentKey + "_" + k, k, result);
    }

    return result;
}

function stringifyJson(object) {
    return JSON.stringify(object, null, 4);
}

function stringifyIniExport(jsonObject) {
    return Object.keys(jsonObject)
        .map((key) => key + "=" + JSON.stringify(jsonObject[key]))
        .join("\n");
}

function stringifyCsvExport(jsonObject) {
    return Object.keys(jsonObject)
        .map((key) => key + ";" + jsonObject[key])
        .join("\n");
}

/**
 * Exports translation keys to *.ini format.
 *
 * @param   {String}    language
 * @returns {String}
 */
export const exportTranslationKeysToIni = (language) => {
    let result = {};
    let englishTranslations = translationsProvider.getDefaultTranslations(languages.ENGLISH_GB);
    let defaultTranslations = translationsProvider.getDefaultTranslations(language);
    let translations = mergeDefaultTranslations(englishTranslations, defaultTranslations);

    let key;
    for (key of Object.keys(translations)) {
        result = getChildExport(translations, key, key, result);
    }

    return getExportFileDescription(language) + stringifyIniExport(sortObjectKeys(result));
};

/**
 * Exports translation keys to *.csv format.
 *
 * @param   {String}    language
 * @returns {String}
 */
export const exportTranslationKeysToCsv = (language) => {
    let result = {};
    let englishTranslations = translationsProvider.getDefaultTranslations(languages.ENGLISH_GB);
    let defaultTranslations = translationsProvider.getDefaultTranslations(language);
    let translations = mergeDefaultTranslations(englishTranslations, defaultTranslations);

    let key;
    for (key of Object.keys(translations)) {
        result = getChildExport(translations, key, key, result);
    }

    return stringifyCsvExport(sortObjectKeys(result));
};

function getUntranslatedValues(english, key, translation) {
    if (typeof english[key] === "string") {
        if (translation && translation[key] && translation[key] !== english[key]) {
            return null;
        }

        return english[key];
    }

    if (translation[key] === undefined) {
        return sortObjectKeys(english[key]);
    }

    let result = {};

    let k;
    let newTranslation;
    for (k of Object.keys(english[key])) {
        newTranslation = getUntranslatedValues(english[key], k, translation[key]);

        if (newTranslation !== null && (typeof english[key] === "string" || Object.keys(newTranslation).length > 0)) {
            result[k] = newTranslation;
        }
    }

    return sortObjectKeys(result);
}

function getNewTranslations(defaultTranslation, translations) {
    let result = {};

    let key;
    let newTranslations;
    for (key of Object.keys(defaultTranslation)) {
        newTranslations = getUntranslatedValues(defaultTranslation, key, translations);

        if (Object.keys(newTranslations).length > 0) {
            result[key] = newTranslations;
        }
    }

    return sortObjectKeys(result);
}

/**
 * Returns array of translation keys that are identical with competent English translations.
 *
 * @param   {String}    language
 * @returns {Array}
 */
function getUniversalKeys(language) {
    switch (language) {
        case languages.CZECH:
            return [
                "clientServerSettings_port",
                "general_delta",
                "general_email",
                "general_frontdesk",
                "general_index",
                "general_interval",
                "general_limit",
                "general_ok",
                "general_reset",
                "general_restart",
                "help_spectroSettings_model",
                "helperDialog_btnOk",
                "importData_import",
                "navigation_menu_formulaData",
                "posInfo_url",
                "pricing_export",
                "pricing_import",
                "printTemplate_bfs",
                "rccDriverSettings_host",
                "rccSpectroSettings_comPort",
                "searchPanel_standard",
                "server_ip",
                "server_server"
            ];
        case languages.GERMAN:
            return [
                "clientServerSettings_offlineStatus",
                "clientServerSettings_onlineStatus",
                "clientServerSettings_port",
                "customRedlike_uuid",
                "customRedlikeSettings_barcode",
                "customRedlikeSettings_offlineStatus",
                "customRedlikeSettings_onlineStatus",
                "filtering_filter",
                "general_delta",
                "general_email",
                "general_frontdesk",
                "general_id",
                "general_in",
                "general_index",
                "general_limit",
                "general_minutes",
                "general_ok",
                "general_reset",
                "helperDialog_btnOk",
                "importData_format",
                "importData_import",
                "infoType_hbw",
                "pricing_export",
                "pricing_import",
                "printTemplate_bfs",
                "rccButtons_ok",
                "rccDriverSettings_host",
                "searchPanel_standard",
                "server_ip",
                "server_server",
                "spectroTab_filter",
                "spectroTab_tolerance",
                "userSettings_barcode"
            ];
        case languages.SLOVAK:
            return [
                "clientServerSettings_port",
                "customRedlikeSettings_offlineStatus",
                "customRedlikeSettings_onlineStatus",
                "environmentsSettings_changelog",
                "filtering_filter",
                "general_delta",
                "general_id",
                "general_index",
                "general_interval",
                "general_limit",
                "general_maximum",
                "general_minimum",
                "general_ok",
                "helperDialog_btnOk",
                "importData_import",
                "posInfo_url",
                "pricing_export",
                "pricing_import",
                "printTemplate_bfs",
                "rccDriverSettings_host",
                "rccSpectroSettings_comPort",
                "rccSpectroSettings_model",
                "server_ip",
                "server_server",
                "serverPlugins_plugin"
            ];
        default:
            return [];
    }
}

/**
 * Exports new translation keys to CSV format.
 *
 * @param   {String}    language
 * @returns {String}
 */
export const exportNewTranslationKeysToCsv = (language) => {
    let result = {};
    let englishTranslations = translationsProvider.getDefaultTranslations(languages.ENGLISH_GB);
    let defaultTranslations = translationsProvider.getDefaultTranslations(language);
    let translations = getNewTranslations(englishTranslations, defaultTranslations);

    let key;
    for (key of Object.keys(translations)) {
        result = getChildExport(translations, key, key, result);
    }

    let universalKeys = getUniversalKeys(language);

    for (key of Object.keys(result)) {
        if (key.startsWith("keyWords_") || key.startsWith("languages_") || universalKeys.includes(key)) {
            delete result[key];
        }
    }

    return stringifyCsvExport(result);
};

function createRecursiveKeys(result, keys, value) {
    if (keys.length === 1) {
        try {
            result[keys[0]] = value;
        } catch (e) {
            return {};
        }

        return result;
    }

    if (!result[keys[0]]) {
        result[keys[0]] = createRecursiveKeys({}, keys.slice(1, keys.length), value);
        return result;
    }

    result[keys[0]] = createRecursiveKeys(result[keys[0]], keys.slice(1, keys.length), value);
    return result;
}

function isParameterValid(value) {
    if (value.match(/[%{}]/) === null) {
        return true;
    }

    let parameters = value.match(/{/g);

    if (parameters === null) {
        return true;
    }

    let validParameters = value.match(/%{[a-zA-Z0-9]+}/g);

    if (validParameters === null) {
        return false;
    }

    return validParameters.length === parameters.length;
}

/**
 * Reads *.ini string and creates JSON object with translation keys.
 *
 * @param   {String} translations
 * @param   {String} language
 * @returns {String}
 */
export const parseIniTranslationKeys = (translations, language) => {
    let rows = translations.split("\n");
    rows = rows.slice(6, rows.length);

    rows.sort(function (a, b) {
        if (a < b) {
            return -1;
        }

        if (a > b) {
            return 1;
        }

        return 0;
    });

    let row,
        keyValue,
        keys,
        result = {};

    for (row of rows) {
        keyValue = row.split("=");

        if (keyValue.length === 2 && isParameterValid(keyValue[1])) {
            keys = keyValue[0].split("_");

            if (keys[0] !== "") {
                result = createRecursiveKeys(
                    result,
                    keyValue[0].split("_"),
                    keyValue[1].substring(1, keyValue[1].length - 1)
                );
            }
        }
    }

    let defaultTranslations = translationsProvider.getDefaultTranslations(language);

    let englishTranslations = translationsProvider.getDefaultTranslations(languages.ENGLISH_GB);

    let defaultResult = mergeDefaultTranslations(defaultTranslations, result);

    let finalResult = mergeDefaultTranslations(englishTranslations, defaultResult);

    let validation = validateTranslationParams(englishTranslations, finalResult);

    if (validation !== null) {
        return validation;
    }

    return stringifyJson(finalResult);
};

/**
 * Reads *.csv string and creates JSON object with translation keys.
 *
 * @param   {String} translations
 * @param   {String} language
 * @returns {String}
 */
export const parseCsvTranslationKeys = (translations, language) => {
    let rows = translations.split("\n");

    rows.sort(function (a, b) {
        if (a < b) {
            return -1;
        }

        if (a > b) {
            return 1;
        }

        return 0;
    });

    let row,
        keyValue,
        keys,
        result = {};

    for (row of rows) {
        if (row.includes('"')) {
            return "Data contains quotation marks:\n\n" + row;
        }

        keyValue = row.split(";");

        if (keyValue.length !== 2) {
            if (keyValue.length < 2) {
                if (row.length === 0) {
                    return "Data contains empty line";
                }

                return "Wrong key;value pair:\n\n" + row;
            }

            return "Multiple semicolon separators:\n\n" + row;
        }

        if (!isParameterValid(keyValue[1])) {
            return "Wrong format of parameter %{[a-zA-Z0-9]+}:\n\n" + row;
        }

        keys = keyValue[0].split("_");

        if (keys[0] !== "") {
            result = createRecursiveKeys(result, keyValue[0].split("_"), keyValue[1]);
        }
    }

    let defaultTranslations = translationsProvider.getDefaultTranslations(language);

    let englishTranslations = translationsProvider.getDefaultTranslations(languages.ENGLISH_GB);

    let defaultResult = mergeDefaultTranslations(defaultTranslations, result);

    let finalResult = mergeDefaultTranslations(englishTranslations, defaultResult);

    let validation = validateTranslationParams(englishTranslations, finalResult);

    if (validation !== null) {
        return validation;
    }

    return stringifyJson(finalResult);
};

const getNestedTranslations = (defaultTranslations, key, customTranslations) => {
    if (typeof defaultTranslations[key] === "string") {
        if (customTranslations !== undefined && typeof customTranslations[key] === "string") {
            return customTranslations[key];
        }

        return defaultTranslations[key];
    }

    if (customTranslations[key] === undefined) {
        return defaultTranslations[key];
    }

    let result = {};

    let k;
    for (k in defaultTranslations[key]) {
        result[k] = getNestedTranslations(defaultTranslations[key], k, customTranslations[key]);
    }

    return result;
};

function getMergedTranslations(languageCode, customTranslations) {
    if (customTranslations === null) {
        return translationsProvider.getDefaultTranslations(languageCode);
    }

    let defaultTranslations = translationsProvider.getDefaultTranslations(languageCode);

    let result = {};

    let key;
    for (key in defaultTranslations) {
        result[key] = getNestedTranslations(defaultTranslations, key, customTranslations);
    }

    return result;
}

function mergeTranslations(redlikeLanguages, customTranslations) {
    let mergedTranslations = {};

    let language;
    for (language of redlikeLanguages) {
        // Set custom translations
        mergedTranslations[language.code] = getMergedTranslations(
            language.code,
            customTranslations === null ? null : customTranslations[language.code]
        );
        // Use English translations as default for missing translation keys
        mergedTranslations[language.code] = getMergedTranslations(
            languages.ENGLISH_GB,
            mergedTranslations[language.code]
        );
    }

    return mergedTranslations;
}

function validateSelectedLanguage(redlikeLanguages, selectedLanguage) {
    if (!redlikeLanguages.some((language) => language.compareTo(selectedLanguage))) {
        return languages.ENGLISH_GB;
    }

    return selectedLanguage;
}

/**
 * Restores NumberFormat which is replaced by NumberFormatConstructor in case of unsupported locales, f.e. Icelandic.
 */
export const restoreNumberFormat = () => {
    Intl.NumberFormat = validNumberFormat;
};

/**
 * Validates input language and sets Redlike language.
 *
 * @param {Array}   redlikeLanguages
 * @param {String}  selectedLanguage
 */
export const setRedlikeLocale = (redlikeLanguages, selectedLanguage) => {
    const validLang = validateSelectedLanguage(redlikeLanguages, selectedLanguage);

    restoreNumberFormat();

    setLocale(validLang, true);
    setHandleMissingTranslation((text) => text);
};

/**
 * @returns {String}
 */
export const getRedlikeLocale = () => {
    return getLocale();
};

/**
 * Initializes translations and sets default language. It merges custom translations with defualt translations.
 *
 * @param {Array}   redlikeLanguages
 * @param {String}  selectedLanguage
 * @param {Object}  customTranslations
 */
export const initializeTranslations = (redlikeLanguages, selectedLanguage, customTranslations) => {
    setTranslations(mergeTranslations(redlikeLanguages, customTranslations), false);
    setRedlikeLocale(redlikeLanguages, selectedLanguage);
};

export const getNavigationItemsTranslation = (option) => {
    switch (option) {
        case settings.NAV_ITEM_BARCODE_SCAN:
            return translate("environmentsSettings.barcodeScan");
        case settings.NAV_ITEM_CANISTER_BARCODE:
            return translate("environmentsSettings.canisterBarcode");
        case settings.NAV_ITEM_CANISTER_LEVEL:
            return translate("environmentsSettings.canisterLevel");
        case settings.NAV_ITEM_CANISTERS:
            return translate("general.canisters");
        case settings.NAV_ITEM_CLEAN:
            return translate("general.clean");
        case settings.NAV_ITEM_CUSTOM_FORMULA:
            return translate("general.customFormula");
        case settings.NAV_ITEM_CUSTOMERS:
            return translate("general.customers");
        case settings.NAV_ITEM_COLOR_SEARCH_TAB:
            return translate("navigation.menu.colorSearch");
        case settings.NAV_ITEM_COLOR_TAB:
            return translate("environmentsSettings.colorPanelNavItem");
        case settings.NAV_ITEM_COLORANT_REFILL_HISTORY:
            return translate("navigation.menu.colorantRefillHistory");
        case settings.NAV_ITEM_FAVORITES:
            return translate("navigation.menu.favorites");
        case settings.NAV_ITEM_HIDE_ITEMS:
            return translate("navigation.navBar.hideItems");
        case settings.NAV_ITEM_HELP:
            return translate("navigation.navBar.help");
        case settings.NAV_ITEM_MACHINE_RESET:
            return translate("environmentsSettings.machineReset");
        case settings.NAV_ITEM_MATCHING:
            return translate("general.matching");
        case settings.NAV_ITEM_TICKETING:
            return translate("general.ticketing");
        case settings.NAV_ITEM_MY_ORDERS:
            return translate("general.myOrders");
        case settings.NAV_ITEM_ORDERS_HISTORY:
            return translate("environmentsSettings.ordersHistory");
        case settings.NAV_ITEM_PURGE:
            return translate("general.purge");
        case settings.NAV_ITEM_QUICK_TINT:
            return translate("general.quickTint");
        case settings.NAV_ITEM_SEARCH_NEW_FORMULA:
            return translate("navigation.tabBar.formulaSearch");
        case settings.NAV_ITEM_SPECTRO:
            return translate("general.spectro");
        case settings.NAV_ITEM_STATISTICS:
            return translate("navigation.menu.dispensingHistory");
        case settings.NAV_ITEM_STIR:
            return translate("environmentsSettings.stir");
        case settings.NAV_ITEM_TO_DISPENSE:
            return translate("general.toDispenseOrders");
        default:
            return option;
    }
};

export const getNavigationItemsHintTranslation = (option, softwareName) => {
    switch (option) {
        case settings.NAV_ITEM_BARCODE_SCAN:
            return translate("help.navigation.barcodeScan");
        case settings.NAV_ITEM_CANISTER_BARCODE:
            return translate("help.navigation.canisterBarcodes");
        case settings.NAV_ITEM_CANISTER_LEVEL:
            return translate("help.navigation.canisterLevel");
        case settings.NAV_ITEM_CANISTERS:
            return translate("help.navigation.canisters");
        case settings.NAV_ITEM_CLEAN:
            return translate("help.navigation.clean");
        case settings.NAV_ITEM_COLOR_SEARCH_TAB:
            return translate("help.navigation.colorSearchTab");
        case settings.NAV_ITEM_COLOR_TAB:
            return translate("help.navigation.colorPanel");
        case settings.NAV_ITEM_CUSTOM_FORMULA:
            return translate("help.navigation.customFormula");
        case settings.NAV_ITEM_CUSTOMERS:
            return translate("help.navigation.customers");
        case settings.NAV_ITEM_FAVORITES:
            return translate("help.navigation.favorites");
        case settings.NAV_ITEM_HIDE_ITEMS:
            return translate("help.navigation.hideItems");
        case settings.NAV_ITEM_MACHINE_RESET:
            return translate("help.navigation.machineReset");
        case settings.NAV_ITEM_MATCHING:
            return translate("help.navigation.matching");
        case settings.NAV_ITEM_TICKETING:
            return translate("help.navigation.ticketing");
        case settings.NAV_ITEM_MY_ORDERS:
            return translate("help.navigation.myOrders");
        case settings.NAV_ITEM_ORDERS_HISTORY:
            return translate("help.navigation.historyOfOrders", {name: softwareName});
        case settings.NAV_ITEM_PURGE:
            return translate("help.navigation.purge");
        case settings.NAV_ITEM_QUICK_TINT:
            return translate("help.navigation.quickTint");
        case settings.NAV_ITEM_SEARCH_NEW_FORMULA:
            return translate("help.navigation.searchFormula");
        case settings.NAV_ITEM_SPECTRO:
            return translate("help.navigation.spectro");
        case settings.NAV_ITEM_STATISTICS:
            return translate("help.navigation.dispensingHistory");
        case settings.NAV_ITEM_STIR:
            return translate("help.navigation.stir");
        case settings.NAV_ITEM_TO_DISPENSE:
            return translate("help.navigation.toDispense");
        case settings.NAV_ITEM_COLORANT_REFILL_HISTORY:
            return translate("help.navigation.colorantRefillHistory");
        default:
            return option;
    }
};

export const getKeyCodesTranslation = (option) => {
    if (option === keyCodes.CTRL) {
        return "CTRL";
    }

    if (option === keyCodes.SHIFT) {
        return "SHIFT";
    }

    if (option === keyCodes.NONE) {
        return "None";
    }

    if (option <= keyCodes.Z) {
        switch (option) {
            case keyCodes.A:
                return "A";
            case keyCodes.B:
                return "B";
            case keyCodes.C:
                return "C";
            case keyCodes.D:
                return "D";
            case keyCodes.E:
                return "E";
            case keyCodes.F:
                return "F";
            case keyCodes.G:
                return "G";
            case keyCodes.H:
                return "H";
            case keyCodes.I:
                return "I";
            case keyCodes.J:
                return "J";
            case keyCodes.K:
                return "K";
            case keyCodes.L:
                return "L";
            case keyCodes.M:
                return "M";
            case keyCodes.N:
                return "N";
            case keyCodes.O:
                return "O";
            case keyCodes.P:
                return "P";
            case keyCodes.Q:
                return "Q";
            case keyCodes.R:
                return "R";
            case keyCodes.S:
                return "S";
            case keyCodes.T:
                return "T";
            case keyCodes.U:
                return "U";
            case keyCodes.V:
                return "V";
            case keyCodes.W:
                return "W";
            case keyCodes.X:
                return "X";
            case keyCodes.Y:
                return "Y";
            case keyCodes.Z:
                return "Z";
            default:
                return option;
        }
    }
    if (option >= keyCodes.NUMPAD_0 && option <= keyCodes.NUMPAD_9) {
        switch (option) {
            case keyCodes.NUMPAD_0:
                return "0";
            case keyCodes.NUMPAD_1:
                return "1";
            case keyCodes.NUMPAD_2:
                return "2";
            case keyCodes.NUMPAD_3:
                return "3";
            case keyCodes.NUMPAD_4:
                return "4";
            case keyCodes.NUMPAD_5:
                return "5";
            case keyCodes.NUMPAD_6:
                return "6";
            case keyCodes.NUMPAD_7:
                return "7";
            case keyCodes.NUMPAD_8:
                return "8";
            case keyCodes.NUMPAD_9:
                return "9";
            default:
                return option;
        }
    }

    if (option >= keyCodes.F1) {
        switch (option) {
            case keyCodes.F1:
                return "F1";
            case keyCodes.F2:
                return "F2";
            case keyCodes.F3:
                return "F3";
            case keyCodes.F4:
                return "F4";
            case keyCodes.F5:
                return "F5";
            case keyCodes.F6:
                return "F6";
            case keyCodes.F7:
                return "F7";
            case keyCodes.F9:
                return "F9";
            case keyCodes.F10:
                return "F10";
            case keyCodes.F11:
                return "F11";
            case keyCodes.F12:
                return "F12";
            default:
                return option;
        }
    }
    return null;
};

export const getKeyboardShortcutsTranslation = (option) => {
    switch (option) {
        case "CTRL":
            return keyCodes.CTRL;
        case "SHIFT":
            return keyCodes.SHIFT;
        case "None":
            return keyCodes.NONE;
        case "A":
            return keyCodes.A;
        case "B":
            return keyCodes.B;
        case "C":
            return keyCodes.C;
        case "D":
            return keyCodes.D;
        case "E":
            return keyCodes.E;
        case "F":
            return keyCodes.F;
        case "G":
            return keyCodes.G;
        case "H":
            return keyCodes.H;
        case "I":
            return keyCodes.I;
        case "J":
            return keyCodes.J;
        case "K":
            return keyCodes.K;
        case "L":
            return keyCodes.L;
        case "M":
            return keyCodes.M;
        case "N":
            return keyCodes.N;
        case "O":
            return keyCodes.O;
        case "P":
            return keyCodes.P;
        case "Q":
            return keyCodes.Q;
        case "R":
            return keyCodes.R;
        case "S":
            return keyCodes.S;
        case "T":
            return keyCodes.T;
        case "U":
            return keyCodes.U;
        case "V":
            return keyCodes.V;
        case "W":
            return keyCodes.W;
        case "X":
            return keyCodes.X;
        case "Y":
            return keyCodes.Y;
        case "Z":
            return keyCodes.Z;
        case "F1":
            return keyCodes.F1;
        case "F2":
            return keyCodes.F2;
        case "F3":
            return keyCodes.F3;
        case "F4":
            return keyCodes.F4;
        case "F5":
            return keyCodes.F5;
        case "F6":
            return keyCodes.F6;
        case "F7":
            return keyCodes.F7;
        case "F9":
            return keyCodes.F9;
        case "F10":
            return keyCodes.F10;
        case "F11":
            return keyCodes.F11;
        case "F12":
            return keyCodes.F12;
        case "0":
            return keyCodes.NUMPAD_0;
        case "1":
            return keyCodes.NUMPAD_1;
        case "2":
            return keyCodes.NUMPAD_2;
        case "3":
            return keyCodes.NUMPAD_3;
        case "4":
            return keyCodes.NUMPAD_4;
        case "5":
            return keyCodes.NUMPAD_5;
        case "6":
            return keyCodes.NUMPAD_6;
        case "7":
            return keyCodes.NUMPAD_7;
        case "8":
            return keyCodes.NUMPAD_8;
        case "9":
            return keyCodes.NUMPAD_9;
        default:
            return option;
    }
};

export const getPriceTypesTranslation = (id, colorant = null) => {
    switch (id) {
        case priceTypes.BASE_PURCHASE:
            return translate("priceTypeList.priceBasePurchase");
        case priceTypes.BASE_SELL_FULL:
            return translate("priceTypeList.priceBaseSellFull");
        case priceTypes.BASE_SELL:
            return translate("priceTypeList.priceBaseSell");
        case priceTypes.CNT_PURCHASE:
            return translate("priceTypeList.priceCntPurchase");
        case priceTypes.CNT_PURCHASE_BY_NAME:
            if (colorant) {
                return translate("priceTypeList.priceCntPurchaseByColorantName", [colorant]);
            }

            return translate("priceTypeList.priceCntPurchaseByName");
        case priceTypes.CNT_SELL_FULL:
            return translate("priceTypeList.priceCntSellFull");
        case priceTypes.CNT_SELL_FULL_BY_NAME:
            if (colorant) {
                return translate("priceTypeList.priceCntSellFullByColorantName", [colorant]);
            }

            return translate("priceTypeList.priceCntSellFullByName");
        case priceTypes.CNT_SELL:
            return translate("priceTypeList.priceCntSell");
        case priceTypes.CNT_SELL_BY_NAME:
            if (colorant) {
                return translate("priceTypeList.priceCntSellByColorantName", [colorant]);
            }

            return translate("priceTypeList.priceCntSellByName");
        case priceTypes.MARGIN_ALL:
            return translate("priceTypeList.priceMarginAll");
        case priceTypes.MARGIN_PER_PKG:
            return translate("priceTypeList.priceMarginPerPkg");
        case priceTypes.PRICE_GROUP:
            return translate("general.priceGroup");
        case priceTypes.PRICE_GROUP_BARCODE:
            return translate("priceTypeList.priceGroupBarcode");
        case priceTypes.SELL_PRICE_ALL:
            return translate("priceTypeList.priceSellPriceAll");
        case priceTypes.SELL_PRICE_PER_L:
            return translate("priceTypeList.priceSellPricePerL");
        case priceTypes.SELL_PRICE_PER_KG:
            return translate("priceTypeList.priceSellPricePerKg");
        case priceTypes.SELL_PRICE_PER_PKG:
            return translate("priceTypeList.priceSellPricePerPkg");
        case priceTypes.SURCHARGE_ALL:
            return translate("priceTypeList.priceSurchargeAll");
        case priceTypes.SURCHARGE_PER_PKG:
            return translate("priceTypeList.priceSurchargePerPkg");
        case priceTypes.TINT_PURCHASE_ALL:
            return translate("priceTypeList.priceTintPurchaseAll");
        case priceTypes.TINT_PURCHASE_PER_PKG:
            return translate("priceTypeList.priceTintPurchasePerPkg");
        case priceTypes.TINT_SELL_FULL_ALL:
            return translate("priceTypeList.priceTintSellFullAll");
        case priceTypes.TINT_SELL_FULL_PER_PKG:
            return translate("priceTypeList.priceTintSellFullAllPerPackage");
        case priceTypes.TOTAL_PURCHASE_ALL:
            return translate("priceTypeList.priceTotalPurchaseAll");
        case priceTypes.TOTAL_PURCHASE_PER_L:
            return translate("priceTypeList.priceTotalPurchasePerL");
        case priceTypes.TOTAL_PURCHASE_PER_KG:
            return translate("priceTypeList.priceTotalPurchasePerKg");
        case priceTypes.TOTAL_PURCHASE_PER_PKG:
            return translate("priceTypeList.priceTotalPurchasePerPkg");
        case priceTypes.TOTAL_SELL_FULL_ALL:
            return translate("priceTypeList.priceTotalSellFullAll");
        case priceTypes.TOTAL_SELL_FULL_PER_L:
            return translate("priceTypeList.priceTotalSellFullPerLiter");
        case priceTypes.TOTAL_SELL_FULL_PER_KG:
            return translate("priceTypeList.priceTotalSellFullPerKg");
        case priceTypes.TOTAL_SELL_FULL_PER_PKG:
            return translate("priceTypeList.priceTotalSellFullPerPackage");
        default:
            return id;
    }
};

export const getFormulaInfoDataTranslation = (id) => {
    switch (id) {
        case infoData.TOTAL_PAINT_DENSITY:
            return translate("infoType.totalPaintDensity");
        case infoData.TOTAL_PAINT_VOLUME:
        case infoData.TOTAL_PAINT_VOLUME_L:
            return translate("infoType.totalPaintVolume");
        case infoData.TOTAL_PAINT_MASS:
        case infoData.TOTAL_PAINT_MASS_KG:
            return translate("infoType.totalPaintMass");
        case infoData.BASE_VOLUME:
            return translate("infoType.baseVolume");
        case infoData.BASE_MASS:
            return translate("infoType.baseMass");
        case infoData.COLORANT_VOLUME:
            return translate("infoType.colorantVolume");
        case infoData.COLORANT_MASS:
            return translate("infoType.colorantMass");
        case infoData.BFS_GRADE:
            return translate("infoType.bfsGrade");
        case infoData.HBW:
            return translate("infoType.hbw");
        case infoData.COLOR_RGB:
            return translate("infoType.colorRgb");
        case infoData.COLOR_RGB_HEX:
            return translate("infoType.colorRgbHex");
        case infoData.COLOR_LAB:
            return translate("infoType.colorLab");
        case infoData.COLOR_XYZ:
            return translate("infoType.colorXyz");
        case infoData.Y_VALUE:
            return translate("infoType.yValue");
        case infoData.NOMINAL_VOLUME:
            return translate("infoType.nominalVolume");
        case infoData.NOMINAL_MASS:
            return translate("infoType.nominalMass");
        case infoData.MAX_COLORANT_VOLUME:
            return translate("infoType.maxColorantVolume");
        case infoData.MAX_COLORANT_MASS:
            return translate("infoType.maxColorantMass");
        case infoData.BASE_DENSITY:
            return translate("infoType.baseDensity");
        case infoData.IS_GRAVIMETRIC:
            return translate("infoType.isPackageGravimetric");
        case infoData.OVERFILL:
            return translate("infoType.overfillDetected");
        case infoData.BASE_UFI_CODE:
            return translate("infoType.baseUfiCode");
        case infoData.UFI_CODES_TABLE:
            return translate("infoType.ufiCodesTable");
        case infoData.COLORANT_UFI_CODES:
            return translate("infoType.colorantUfiCodes");
        case infoData.FORMULA_DATE:
            return translate("infoType.formulaDate");
        case infoData.FORMULA_DATETIME:
            return translate("infoType.formulaDatetime");
        case infoData.FORMULA_MORE_INFO:
            return translate("infoType.formulaMoreInfo");
        case infoData.FORMULA_MORE_INFO_PRINT:
            return translate("infoType.formulaMoreInfoPrint");
        case infoData.FORMULA_MORE_INFO_INTERNAL:
            return translate("infoType.formulaMoreInfoInternal");
        case infoData.FORMULA_VERIFIED:
            return translate("infoType.verifiedFormula");
        case infoData.COLORANT_BATCH_NUMBERS:
            return translate("infoType.colorantBatchNumbers");
        case infoData.BATCH_NUMBERS_TABLE:
            return translate("infoType.batchNumbersTable");
        default:
            return id;
    }
};

export const getFormulaInfoDataSettingsTranslation = (id) => {
    switch (id) {
        case infoData.TOTAL_PAINT_VOLUME:
            return translate("infoType.totalPaintVolume") + " (" + settings.UNIT_ML + ")";
        case infoData.TOTAL_PAINT_VOLUME_L:
            return translate("infoType.totalPaintVolume") + " (" + settings.UNIT_L + ")";
        case infoData.TOTAL_PAINT_MASS:
            return translate("infoType.totalPaintMass") + " (" + settings.UNIT_G + ")";
        case infoData.TOTAL_PAINT_MASS_KG:
            return translate("infoType.totalPaintMass") + " (" + settings.UNIT_KG + ")";
        default:
            return getFormulaInfoDataTranslation(id);
    }
};

export const getPriceDefinitionTranslation = (option) => {
    switch (option) {
        case settings.PURCHASE_ONLY:
            return translate("environmentsSettings.priceDefPurchaseOnly");
        case settings.SELL_ONLY:
            return translate("environmentsSettings.priceDefSellOnly");
        case settings.BOTH_PURCHASE_PRIMARY:
            return translate("environmentsSettings.priceDefBothPurchasePrimary");
        case settings.BOTH_SELL_PRIMARY:
            return translate("environmentsSettings.priceDefBothSellPrimary");
        default:
            return option;
    }
};

export const getWeekDaysTranslations = (id) => {
    switch (id) {
        case 0:
            return translate("weekdays.monday");
        case 1:
            return translate("weekdays.tuesday");
        case 2:
            return translate("weekdays.wednesday");
        case 3:
            return translate("weekdays.thursday");
        case 4:
            return translate("weekdays.friday");
        case 5:
            return translate("weekdays.saturday");
        case 6:
            return translate("weekdays.sunday");
        default:
            return "";
    }
};

export const getOrderStatusTranslations = (status) => {
    switch (status) {
        case orderStatus.ORDER_STATUS_CREATED:
            return translate("ordering.status.created");
        case orderStatus.ORDER_STATUS_ACCEPTED:
            return translate("ordering.status.accepted");
        case orderStatus.ORDER_STATUS_PREPARING:
            return translate("ordering.status.preparing");
        case orderStatus.ORDER_STATUS_PARTLY_DONE:
            return translate("ordering.status.partlyDone");
        case orderStatus.ORDER_STATUS_DONE:
            return translate("ordering.status.done");
        case orderStatus.ORDER_STATUS_CANCELED:
            return translate("ordering.status.canceled");
        default:
            return translate("ordering.status.unknown");
    }
};

export const getSettingsMenuTranslations = (item) => {
    switch (item) {
        case resources.MENU_SETTIGNS:
            return translate("navigation.menu.settings");
        case resources.MENU_INTERFACE_SETTIGNS:
            return translate("navigation.menu.interfaceSettings");
        case resources.MENU_ADVANCED_SETTIGNS:
            return translate("navigation.menu.advancedSettings");
        case resources.MENU_PRICE_SETTIGNS:
            return translate("navigation.menu.priceSettings");
        case resources.SUBMENU_PRICING_OPTIONS:
            return translate("navigation.menu.pricingOptions");
        case resources.SUBMENU_DEFAULT_PRICE_VALUES:
            return translate("navigation.menu.defaultValues");
        case resources.MENU_USER_MANAGEMENT:
            return translate("navigation.menu.userManagemenet");
        case resources.SUBMENU_ENVIRONMENTAL_SETTINGS:
            return translate("navigation.menu.environmentalSettings");
        case resources.SUBMENU_SOFTWARE_OPTIONS:
            return translate("navigation.menu.softwareOptions");
        case resources.SUBMENU_LAYOUT:
            return translate("navigation.menu.layout");
        case resources.SUBMENU_TABS:
            return translate("navigation.menu.tabs");
        case resources.SUBMENU_NAVIGATION_ITEMS:
            return translate("navigation.menu.navigationItems");
        case resources.SUBMENU_FORMULA_SETTINGS:
            return translate("navigation.menu.formulaSettings");
        case resources.SUBMENU_COLORANT:
            return translate("navigation.menu.colorant");
        case resources.SUBMENU_COLOR:
            return translate("navigation.menu.formula");
        case resources.SUBMENU_CUSTOM_FORMULA:
            return translate("formulaSettings.customFormula");
        case resources.SUBMENU_FORMULA_PANELS:
            return translate("navigation.menu.formulaPanels");
        case resources.SUBMENU_GENERAL:
            return translate("serviceActionsSettings.general");
        case resources.SUBMENU_AVAILABLE_PRICES:
            return translate("navigation.menu.availablePrices");
        case resources.SUBMENU_COLOR_PANEL:
            return translate("navigation.menu.colorPanel");
        case resources.SUBMENU_MATCHING_SETTINGS:
            return translate("navigation.menu.matchingSettings");
        case resources.CONTENT_COMPETITIVE_SEARCH:
            return translate("navigation.menu.competitiveSearch");
        case resources.SUBMENU_QUINT_TINT:
            return translate("navigation.menu.quickTint");
        case resources.SUBMENU_QUALITY_CONTROL_SETTINGS:
            return translate("navigation.menu.qualityControlSettings");
        case resources.SUBMENU_ORDER_SETTINGS:
            return translate("navigation.menu.orderSettings");
        case resources.SUBMENU_SEARCH_SETTINGS:
            return translate("navigation.menu.searchSettings");
        case resources.SUBMENU_LANGUAGE_SETTINGS:
            return translate("navigation.menu.languageSettings");
        case resources.SUBMENU_KEYBOARD_SHORTCUTS:
            return translate("navigation.menu.keyboardShortcuts");
        case resources.SUBMENU_COLORANT_PRICING:
            return translate("navigation.menu.colorantPricing");
        case resources.SUBMENU_PACKAGE_PRICING:
            return translate("navigation.menu.packageSurcharge");
        case resources.SUBMENU_PRODUCT_PRICING:
            return translate("navigation.menu.productPricing");
        case resources.SUBMENU_MARGIN_SETTING:
            return translate("navigation.menu.marginAndDiscount");
        case resources.SUBMENU_COLOR_REPRESENTATION:
            return translate("navigation.menu.colorRepresentation");
        case resources.SUBMENU_HELP_SETTINGS:
            return translate("navigation.menu.helpItems");
        case resources.CONTENT_ENVIRONMENT:
            return translate("navigation.menu.environment");
        case resources.CONTENT_VISIBLE_UNITS:
            return translate("navigation.menu.visibleUnits");
        case resources.CONTENT_FORMULA_UNIT:
            return translate("navigation.menu.formulaUnit");
        case resources.CONTENT_INITIAL_TAB:
            return translate("navigation.menu.initialFormulaTab");
        case resources.CONTENT_ADDITIONAL_TAB:
            return translate("navigation.menu.additionalFormulaTab");
        case resources.CONTENT_POP_UPS:
            return translate("navigation.menu.popUps");
        case resources.CONTENT_COLOR_SEARCH:
            return translate("navigation.tabBar.formulaSearch");
        case resources.CONTENT_MATCHING_INFORMATION:
            return translate("matching.matchingInformation");
        case resources.CONTENT_MATCHING_OPTIONS:
            return translate("matching.matchingSearchOptions");
        case resources.CONTENT_MATCHING_RESULT:
            return translate("navigation.menu.matchingSearchResult");
        case resources.CONTENT_MODIFICATION:
            return translate("navigation.menu.formulaModification");
        case resources.CONTENT_QUALITY_CONTROL:
            return translate("general.qualityControl");
        case resources.CONTENT_QUICK_TINT_SORTING:
            return translate("quickTintSettings.sorting");
        case resources.CONTENT_QUICK_TINT_CLOSING:
            return translate("navigation.menu.closing");
        case resources.CONTENT_GENERAL_SEARCH:
            return translate("navigation.menu.generalSearch");
        case resources.CONTENT_ORDER_SEARCH:
            return translate("navigation.menu.orderSearch");
        case resources.CONTENT_LANGUAGE:
            return translate("navigation.menu.language");
        case resources.CONTENT_FORMATTING:
            return translate("navigation.menu.formatting");
        case resources.CONTENT_MAIN_PRICE_SETTINGS:
            return translate("navigation.menu.mainSettings");
        case resources.CONTENT_PRICE_CALCULATION:
            return translate("environmentsSettings.priceCalculation");
        case resources.CONTENT_IMPORT_AND_EXPORT_PRICE:
            return translate("navigation.menu.importAndExport");
        case resources.CONTENT_CURRENCY_SETTINGS:
            return translate("navigation.menu.currency");
        default:
            return item;
    }
};

export const getPrinterTemplateTranslation = () => {
    return {
        txt_base: translate("general.base"),
        txt_baseAltName: translate("general.base"),
        txt_baseCode: translate("printTemplate.baseCode"),
        txt_baseName: translate("printTemplate.baseName"),
        txt_bfs: translate("printTemplate.bfs"),
        txt_buildingSite: translate("general.buildingSite"),
        txt_canName: translate("general.can"),
        txt_color: translate("general.color"),
        txt_colorAltName: translate("general.color"),
        txt_colorantType: translate("printTemplate.colorantType"),
        txt_colorants: translate("general.colorants"),
        txt_colorantsInline: translate("general.colorants"),
        txt_colorCode: translate("general.colorCode"),
        txt_colorName: translate("general.colorName"),
        txt_currDate: translate("general.date"),
        txt_customerCity: translate("printTemplate.customerCity"),
        txt_customerEmail: translate("printTemplate.customerEmail"),
        txt_customerName: translate("printTemplate.customerName"),
        txt_customerPhone: translate("printTemplate.customerPhone"),
        txt_customerStreet: translate("printTemplate.customerStreet"),
        txt_customerZipCode: translate("printTemplate.customerZipCode"),
        txt_destinationOfUse: translate("printTemplate.destinationOfUse"),
        txt_fandeck: translate("general.fandeck"),
        txt_fandeckAltName: translate("general.fandeck"),
        txt_fandeckCode: translate("general.fandeckCode"),
        txt_fandeckName: translate("general.fandeckName"),
        txt_formula: translate("general.formula"),
        txt_formulaInline: translate("general.formula"),
        txt_infoBaseMass: translate("infoType.baseMass"),
        txt_infoBaseVolume: translate("infoType.baseVolume"),
        txt_infoBfsGrade: translate("printTemplate.infoBfsGrade"),
        txt_infoColorLab: translate("infoType.colorLab"),
        txt_infoColorRgb: translate("infoType.colorRgb"),
        txt_infoColorRgbHex: translate("infoType.colorRgbHex"),
        txt_infoColorXyz: translate("infoType.colorXyz"),
        txt_infoColorantMass: translate("infoType.infoColorantMass"),
        txt_infoColorantVolume: translate("infoType.colorantVolume"),
        txt_infoHbw: translate("infoType.hbw"),
        txt_infoTotalPaintDensity: translate("infoType.totalPaintDensity"),
        txt_infoTotalPaintMass: translate("infoType.totalPaintMass"),
        txt_infoTotalPaintVolume: translate("infoType.totalPaintVolume"),
        txt_infoYValue: translate("infoType.yValue"),
        txt_orderQrCode: translate("printTemplate.orderQrCode"),
        txt_packageOrderCount: translate("printTemplate.packageOrderCount"),
        txt_package: translate("general.package"),
        txt_packageCount: translate("general.numberOfPackages"),
        txt_packageName: translate("general.package"),
        txt_posAddress: translate("printTemplate.posAddress"),
        txt_posAddressSecond: translate("printTemplate.posAddressSecond"),
        txt_posCity: translate("general.posCity"),
        txt_posContact: translate("printTemplate.posContact"),
        txt_posEmail: translate("printTemplate.posEmail"),
        txt_posPhone: translate("printTemplate.posPhone"),
        txt_posUrl: translate("printTemplate.posUrl"),
        txt_posZipCode: translate("printTemplate.posZipCode"),
        txt_priceBasePurchase: translate("priceTypeList.priceBasePurchase"),
        txt_priceBasePurchaseWithTax: translate("printTemplate.priceBasePurchaseWithTax"),
        txt_priceBaseSell: translate("priceTypeList.priceBaseSell"),
        txt_priceBaseSellWithTax: translate("printTemplate.priceBaseSellWithTax"),
        txt_priceCntPurchase: translate("priceTypeList.priceCntPurchase"),
        txt_priceCntPurchaseWithTax: translate("printTemplate.priceCntPurchaseWithTax"),
        txt_priceCntTotalSell: translate("priceTypeList.priceCntSell"),
        txt_priceCntTotalSellWithTax: translate("printTemplate.priceCntTotalSellWithTax"),
        txt_priceMarginAll: translate("priceTypeList.priceMarginAll"),
        txt_priceMarginAllWithTax: translate("printTemplate.priceMarginAllWithTax"),
        txt_priceMarginPerPkg: translate("priceTypeList.priceMarginPerPkg"),
        txt_priceMarginPerPkgWithTax: translate("printTemplate.priceMarginPerPkgWithTax"),
        txt_pricePriceGroup: translate("general.priceGroup"),
        txt_priceSellPriceAll: translate("priceTypeList.priceSellPriceAll"),
        txt_priceSellPriceAllWithTax: translate("printTemplate.priceSellPriceAllWithTax"),
        txt_priceSellPricePerKg: translate("priceTypeList.priceSellPricePerKg"),
        txt_priceSellPricePerKgWithTax: translate("printTemplate.priceSellPricePerKgWithTax"),
        txt_priceSellPricePerL: translate("priceTypeList.priceSellPricePerL"),
        txt_priceSellPricePerLWithTax: translate("printTemplate.priceSellPricePerLWithTax"),
        txt_priceSellPricePerPkg: translate("priceTypeList.priceSellPricePerPkg"),
        txt_priceSellPricePerPkgWithTax: translate("printTemplate.priceSellPricePerPkgWithTax"),
        txt_priceSurchargeAll: translate("priceTypeList.priceSurchargeAll"),
        txt_priceSurchargeAllWithTax: translate("printTemplate.priceSurchargeAllWithTax"),
        txt_priceSurchargePerPkg: translate("priceTypeList.priceSurchargePerPkg"),
        txt_priceSurchargePerPkgWithTax: translate("printTemplate.priceSurchargePerPkgWithTax"),
        txt_priceTintPurchaseAll: translate("priceTypeList.priceTintPurchaseAll"),
        txt_priceTintPurchaseAllWithTax: translate("printTemplate.priceTintPurchaseAllWithTax"),
        txt_priceTintPurchasePerPkg: translate("priceTypeList.priceTintPurchasePerPkg"),
        txt_priceTintPurchasePerPkgWithTax: translate("printTemplate.priceTintPurchasePerPkgWithTax"),
        txt_priceTotalPurchaseAll: translate("priceTypeList.priceTotalPurchaseAll"),
        txt_priceTotalPurchaseAllWithTax: translate("printTemplate.priceTotalPurchaseAllWithTax"),
        txt_priceTotalPurchasePerKg: translate("priceTypeList.priceTotalPurchasePerKg"),
        txt_priceTotalPurchasePerKgWithTax: translate("printTemplate.priceTotalPurchasePerKgWithTax"),
        txt_priceTotalPurchasePerL: translate("priceTypeList.priceTotalPurchasePerL"),
        txt_priceTotalPurchasePerLWithTax: translate("printTemplate.priceTotalPurchasePerLWithTax"),
        txt_priceTotalPurchasePerPkg: translate("priceTypeList.priceTotalPurchasePerPkg"),
        txt_priceTotalPurchasePerPkgWithTax: translate("printTemplate.priceTotalPurchasePerPkgWithTax"),
        txt_product: translate("general.product"),
        txt_productAltName: translate("printTemplate.productAltName"),
        txt_productCode: translate("general.productCode"),
        txt_productName: translate("general.productName"),
        txt_pump1: translate("general.pump1"),
        txt_pump2: translate("general.pump2"),
        txt_recipeInline: translate("printTemplate.recipeInline"),
        txt_tintometricSystem: translate("general.tintometricSystem")
    };
};

export const getHelperDialogHeaderTranslations = (dialog) => {
    if (!dialog) {
        return "";
    }

    switch (dialog.message) {
        case helper.HELPER_MESSAGE_IDLE:
        case helper.HELPER_MESSAGE_IDLE_ABORTED_DOWNLOAD:
        case helper.HELPER_MESSAGE_IDLE_ABORTED_INSTALLATION:
            return translate("helperDialog.yourSoftwareVersion", [dialog.meta.version]);
        case helper.HELPER_MESSAGE_CHECKING_FOR_UPDATE:
            return translate("helperDialog.checkingForUpdates");
        case helper.HELPER_MESSAGE_NEW_UPDATE_AVAILABLE:
            return translate("helperDialog.updateAvailable");
        case helper.HELPER_MESSAGE_NO_UPDATE_AVAILABLE:
            return translate("helperDialog.yourSoftwareIsUpToDate");
        case helper.HELPER_MESSAGE_ERROR_DURING_CHECKING:
            return translate("general.error");
        case helper.HELPER_MESSAGE_SYNCHRONIZING_DATABASES:
            return translate("helperDialog.synchronizingDatabases");
        case helper.HELPER_MESSAGE_DOWNLOADING_PACKAGE:
            return translate("helperDialog.downloadingPackage");
        case helper.HELPER_MESSAGE_VERIFYING_PACKAGE:
            return translate("helperDialog.verifyingPackage");
        case helper.HELPER_MESSAGE_DOWNLOADING_FAILED:
            return translate("helperDialog.downloadingFailed");
        case helper.HELPER_MESSAGE_VERIFICATION_FAILED:
            return translate("helperDialog.verificationFailed");
        case helper.HELPER_MESSAGE_START_INSTALLATION:
            return translate("helperDialog.updateDownloaded");
        case helper.HELPER_MESSAGE_AUTOMATIC_INSTALLATION:
            return translate("helperDialog.automaticInstallation");
        case helper.HELPER_MESSAGE_STOPPING_SERVICES:
            return translate("helperDialog.stoppingComponents");
        case helper.HELPER_MESSAGE_COPYING_FILES:
            return translate("helperDialog.installingUpdate");
        case helper.HELPER_MESSAGE_STARTING_SERVICES:
            return translate("helperDialog.startingComponents");
        case helper.HELPER_MESSAGE_UPDATE_FINISHING:
            return translate("helperDialog.finishingUpdate");
        case helper.HELPER_MESSAGE_UPDATE_DONE:
            return translate("helperDialog.updateInstalled");
        case helper.HELPER_MESSAGE_UPDATE_FAILED:
            return translate("helperDialog.updateFailed");
        case helper.HELPER_MESSAGE_REBOOT_REQUIRED:
            return translate("helperDialog.rebootRequired");
        default:
            return translate("helperDialog.internalError");
    }
};

export const getHelperButtonTranslations = (button) => {
    switch (button) {
        case helper.HELPER_BUTTON_ABORT:
            return translate("helperDialog.btnAbort");
        case helper.HELPER_BUTTON_CANCEL:
            return translate("helperDialog.btnCancel");
        case helper.HELPER_BUTTON_CHECK_UPDATE:
            return translate("helperDialog.btnCheckUpdate");
        case helper.HELPER_BUTTON_CONTINUE:
            return translate("helperDialog.btnContinue");
        case helper.HELPER_BUTTON_DOWNLOAD_UPDATE:
            return translate("helperDialog.btnDownloadUpdate");
        case helper.HELPER_BUTTON_INSTALL_UPDATE:
            return translate("helperDialog.btnInstallUpdate");
        case helper.HELPER_BUTTON_OK:
            return translate("helperDialog.btnOk");
        case helper.HELPER_BUTTON_RETRY:
            return translate("helperDialog.btnRetry");
        case helper.HELPER_BUTTON_SKIP:
            return translate("helperDialog.btnSkip");
        default:
            return "";
    }
};

export const getColorTabOrderByTranslation = (item) => {
    switch (item) {
        case settings.ORDER_BY_COLOR_NAME:
            return translate("general.colorName");
        case settings.ORDER_BY_COLOR_CODE:
            return translate("general.colorCode");
        case settings.ORDER_BY_COLOR_HUE:
            return translate("colorTab.colorTone");
        case settings.ORDER_BY_FANDECK_POSITION:
            return translate("colorTab.fandeckPosition");
        default:
            return "";
    }
};

export const getColorantPanelOrderByTranslation = (item) => {
    switch (item) {
        case settings.ORDER_BY_NAME:
            return translate("general.nameTitle");
        case settings.ORDER_BY_AMOUNT:
            return translate("general.amount");
        default:
            return translate("general.default");
    }
};

export const getTilePreviewSizeTranslation = (item) => {
    switch (item) {
        case settings.TILE_GRID_40:
            return "10x4";
        case settings.TILE_GRID_20:
            return "10x2";
        case settings.TILE_GRID_84:
            return "12x7";
        case settings.TILE_GRID_150:
            return "15x10";
        default:
            return "";
    }
};

export const translateTabName = (type, scene = null) => {
    switch (type) {
        case componentTypes.MAIN_TAB:
        case componentTypes.INITIAL_TAB:
            return translate("navigation.tabBar.formulaSearch");
        case componentTypes.BUG_REPORT_TAB:
            return translate("helper.bugReport");
        case componentTypes.COLOR_SEARCH_TAB:
            return translate("navigation.menu.colorSearch");
        case componentTypes.COLOR_TAB:
            return translate("environmentsSettings.colorPanelNavItem");
        case componentTypes.CUSTOMERS_TAB:
            return translate("general.customers");
        case componentTypes.CUSTOM_TAB:
            return translate("navigation.tabBar.customFormula");
        case componentTypes.CANISTERS_TAB:
            return translate("general.canisters");
        case componentTypes.FAVORITES_TAB:
            return translate("navigation.tabBar.favorites");
        case componentTypes.GLOBAL_SEARCH_TAB:
            return translate("navigation.globalSearch.searchInFormulas") + "...";
        case componentTypes.MATCHING_TAB:
            if (scene === componentTypes.AUTO_CORRECTION_SCENE) {
                return translate("general.correction");
            }

            if (scene === componentTypes.DRAFT_FORMULA_SCENE) {
                return translate("matching.draftFormula");
            }

            return translate("general.matching");
        case componentTypes.MY_ORDERS_TAB:
            return translate("general.myOrders");
        case componentTypes.ORDERS_HISTORY_TAB:
            return translate("customRedlike.historyOfOrders");
        case componentTypes.SPECTRO_TAB:
            return translate("general.spectro");
        case componentTypes.STATISTICS_TAB:
            return translate("navigation.tabBar.dispensingHistory");
        case componentTypes.TICKETING_TAB:
            return translate("general.ticketing");
        case componentTypes.TO_DISPENSE_TAB:
            return translate("general.toDispenseOrders");
        default:
            return "";
    }
};

export const translateImportDataResultCode = (resultCode) => {
    switch (resultCode) {
        case resultCodes.RESULT_OK:
            return translate("importData.importedWithErrors");
        case resultCodes.RESULT_IMPORT_FILE_MISSING:
            return translate("importData.fileMissing");
        case resultCodes.RESULT_IMPORT_DATA_MISSING:
            return translate("importData.dataMissing");
        case resultCodes.RESULT_IMPORT_UNKNOWN_FORMAT:
            return translate("importData.dataMissing");
        case resultCodes.RESULT_IMPORT_ABORTED:
            return translate("importData.aborted");
        case resultCodes.RESULT_IMPORT_UNDER_PROGRESS:
            return translate("importData.underProgress");
        case resultCodes.RESULT_IMPORT_HEADER_MISSING:
            return translate("importData.headerMissing");
        default:
            return translate("importData.importFailed");
    }
};

export const getSearchPanelOrderTranslation = (key) => {
    switch (key) {
        case componentTypes.SEARCH_PANEL_PRODUCT:
            return translate("general.product");
        case componentTypes.SEARCH_PANEL_FANDECK:
            return translate("general.fandeck");
        case componentTypes.SEARCH_PANEL_FORMULA:
            return translate("general.color");
        case componentTypes.SEARCH_PANEL_FORMULA_NAME:
            return translate("general.colorName");
        case componentTypes.SEARCH_PANEL_FORMULA_CODE:
            return translate("general.colorCode");
        case componentTypes.SEARCH_PANEL_BASE:
            return translate("general.base");
        case componentTypes.SEARCH_PANEL_BIPP:
            return translate("general.baseInProductPackage");
        default:
            return key;
    }
};

export const getSetupWizardStepTranslation = (key) => {
    switch (key) {
        case setup.LANGUAGE:
            return translate("navigation.menu.language");
        case setup.LICENSING:
            return translate("navigation.menu.license");
        case setup.DATABASE:
            return translate("navigation.menu.database");
        case setup.LOGIN:
            return translate("login.logIn");
        case setup.MACHINE:
            return translate("general.machine");
        case setup.PRINTER:
            return translate("print.choosePrinter");
        case setup.SPECTRO:
            return translate("general.spectro");
        case setup.SETTINGS:
            return translate("navigation.menu.environment");
        case setup.SUMMARY:
            return translate("general.summary");
        default:
            return key;
    }
};

export const getDbStatusTranslation = (key) => {
    switch (key) {
        case statuses.OK:
            return translate("general.ok");
        case statuses.MAIN_AND_LOCAL_UPDATE_NEEDED:
            return translate("welcome.availableDatabaseMainAndLocalUpdate");
        case statuses.MAIN_UPDATE_NEEDED:
            return translate("welcome.availableDatabaseMainUpdate");
        case statuses.LOCAL_UPDATE_NEEDED:
            return translate("welcome.availableDatabaseLocalUpdate");
        case statuses.MAIN_AND_LOCAL_MISSING:
            return translate("welcome.availableDatabaseMainAndLocalMissing");
        case statuses.MAIN_MISSING:
            return translate("welcome.availableDatabaseMainMissing");
        case statuses.LOCAL_MISSING:
            return translate("welcome.availableDatabaseLocalMissing");
        case statuses.MAIN_AND_LOCAL_ERROR:
            return translate("welcome.availableDatabaseMainAndLocalError");
        case statuses.MAIN_ERROR:
            return translate("welcome.availableDatabaseMainError");
        case statuses.LOCAL_ERROR:
            return translate("welcome.availableDatabaseLocalError");
        case statuses.MAIN_PARTIAL_UPDATE_NEEDED:
            return translate("welcome.availableDatabaseMainPartialUpdate");
        case statuses.MAIN_PARTIAL_UPDATE_AND_LOCAL_UPDATE_NEEDED:
            return translate("welcome.availableDatabaseMainPartialUpdateAndLocalUpdate");
        default:
            return translate("welcome.availableDatabase");
    }
};

export const getBannerPositionTranslation = (option) => {
    switch (option) {
        case settings.CUSTOM_BANNER_POSITION_TOP:
            return translate("general.top");
        case settings.CUSTOM_BANNER_POSITION_BOTTOM:
            return translate("general.bottom");
        default:
            return option;
    }
};

export const getResizableTranslation = (option) => {
    switch (option) {
        case settings.RESIZABLE_ON_FOCUS:
            return translate("general.onFocus");
        case settings.RESIZABLE_AVAILABLE:
            return translate("general.available");
        default:
            return option;
    }
};

export const getSpectroTooltipText = (type) => {
    switch (type) {
        case rcc.SPECTRO_TYPE_NONE:
            return translate("help.spectroSettings.spectroTypeNotSelected");
        case rcc.SPECTRO_TYPE_SIMULATION:
            return translate("help.spectroSettings.spectroTypeSimulation");
        case rcc.SPECTRO_TYPE_XRITE:
            return translate("help.spectroSettings.spectroTypeXRite");
        case rcc.SPECTRO_TYPE_MINOLTA:
            return translate("help.spectroSettings.spectroTypeMinolta2XXXd");
        case rcc.SPECTRO_TYPE_DATACOLORREADER:
            return translate("help.spectroSettings.spectroTypeDataColorReader");
        default:
            return translate("help.spectroSettings.spectroType");
    }
};

export const getSectionsDescriptions = (section, softwareName, softwareNameLight) => {
    switch (section) {
        case resources.SETTINGS_DISPENSING_ENABLED:
            return {
                title: translate("environmentsSettings.allowDispensing"),
                description: translate("help.environment.allowDispensing")
            };
        case resources.SETTINGS_ORDERING_ENABLED:
            return {
                title: translate("environmentsSettings.allowOrdering"),
                description: translate("help.environment.allowOrdering")
            };
        case resources.SETTINGS_VISIBLE_UNITS:
            return {
                title: translate("navigation.menu.visibleUnits"),
                description: translate("help.environment.setVisibleUnit")
            };
        case resources.SETTINGS_FORMULA_UNITS:
            return {
                title: translate("navigation.menu.formulaUnit"),
                description: translate("help.environment.setFormulaUnit")
            };
        case resources.PRICE_SETTINGS_LOCAL_PRICES:
            return {
                title: translate("environmentsSettings.showLocalPrices"),
                description: translate("help.priceSettings.localPrices")
            };
        case resources.PRICE_SETTINGS_USE_MARGIN:
            return {
                title: translate("environmentsSettings.margin"),
                description: translate("help.priceSettings.margin")
            };
        case resources.PRICE_SETTINGS_USE_MARKUP:
            return {
                title: translate("environmentsSettings.markup"),
                description: translate("help.priceSettings.markup")
            };
        case resources.PRICE_SETTINGS_USE_DISCOUNT:
            return {
                title: translate("environmentsSettings.discount"),
                description: translate("help.priceSettings.discount")
            };
        case resources.SETTINGS_USER_IDENTIFICATION_ENABLED:
            return {
                title: translate("rccMachineSettings.enableUserIdentification"),
                description: translate("help.machineSettings.enableUserIdentification")
            };
        case resources.SETTINGS_PRODUCT_CODE:
            return {
                title: translate("formulaSettings.useProductCode"),
                description: translate("help.formulaSettings.productCode")
            };
        case resources.SETTINGS_BLEND_FORMULA_AND_CODE:
            return {
                title: translate("formulaSettings.blendColorNameAndCode"),
                description: translate("help.formulaSettings.blendColorNameAndCode")
            };
        case resources.SETTINGS_AUTO_CLOSE_TAB:
            return {
                title: translate("environmentsSettings.allowAutoCloseTab"),
                description: translate("help.environment.allowAutoCloseTab")
            };
        case resources.SETTINGS_PRICING_ENABLED:
            return {
                title: translate("environmentsSettings.allowPricing"),
                description: translate("help.environment.allowPricing")
            };
        case resources.CONTENT_ENVIRONMENT:
            return {
                title: translate("formulaSettings.setCompetitiveColorSearch"),
                description: translate("help.environment.setCompetitiveColorSearch")
            };
        case resources.SETTINGS_BARCODES_ENABLED:
            return {
                title: translate("environmentsSettings.allowBarcodes"),
                description: translate("help.environment.canisterBarcodes")
            };
        case resources.SETTINGS_PRINT_ENABLED:
            return {
                title: translate("environmentsSettings.allowPrint"),
                description: translate("help.environment.printEnabled")
            };
        case resources.SETTINGS_SPECTRO_ENABLED:
            return {
                title: translate("environmentsSettings.allowSpectro"),
                description: translate("help.environment.allowSpectro")
            };
        case resources.SETTINGS_TABS_ENABLED:
            return {
                title: translate("environmentsSettings.allowTabs"),
                description: translate("help.environment.allowTabs")
            };
        case resources.SETTINGS_TABS_MEMORY:
            return {
                title: translate("environmentsSettings.memoryTabs"),
                description: translate("help.environment.memoryTabs")
            };
        case resources.SETTINGS_LIGHT_ENABLED:
            return {
                title: translate("environmentsSettings.allowLight", {name: softwareNameLight}),
                description: translate("help.environment.allowLight", {name: softwareNameLight})
            };
        case resources.CONTENT_ITEM_LIGHT_AT_STARTUP:
            return {
                title: translate("environmentsSettings.runLightAtStartUp", {name: softwareNameLight}),
                description: translate("help.environment.runLightAtStartUp", {
                    light: softwareNameLight,
                    name: softwareName
                })
            };
        case resources.SETTINGS_CUSTOM_HOME_BUTTON:
            return {
                title: translate("environmentsSettings.setCustomHomeButton"),
                description: translate("help.environment.setCustomHomeButton", {name: softwareNameLight})
            };
        case resources.SETTINGS_KIOSK:
            return {
                title: translate("environmentsSettings.setKioskModeOn"),
                description: translate("help.environment.turnOnKioskMode")
            };
        case resources.SETTINGS_AUTOMATIC_CLIENT_START:
            return {
                title: translate("environmentsSettings.setAutomaticClientStart", {name: softwareName}),
                description: translate("help.environment.allowAutomaticClientStart", {name: softwareName})
            };
        case resources.SETTINGS_LOCK_COMPUTER:
            return {
                title: translate("environmentsSettings.lockYourComputer"),
                description: translate("help.environment.lockComputer", {light: softwareNameLight})
            };
        case resources.SETTINGS_VIRTUAL_KEYBOARD_ENABLED:
            return {
                title: translate("environmentsSettings.enableVirtualKeyboard"),
                description: translate("help.environment.enableVirtualKeyboard", {light: softwareNameLight})
            };
        case resources.SETTINGS_CUSTOM_BANNER_POSITION:
            return {
                title: translate("environmentsSettings.customBanner"),
                description: translate("help.environment.customBanner")
            };
        case resources.SETTINGS_ZOOM:
            return {
                title: translate("general.zoom"),
                description: translate("help.environment.applyZoom")
            };
        case resources.SETTINGS_SCREENSAVER_TIMEOUT:
            return {
                title: translate("environmentsSettings.screenSaverTimeout"),
                description: translate("help.environment.screenSaverTimeout")
            };
        case resources.SETTINGS_NUMBER_OF_TABS:
            return {
                title: translate("environmentsSettings.maxNumberTabs"),
                description: translate("help.environment.maxNumberTabs")
            };
        case resources.SETTINGS_INIT_TAB_TYPE:
            return {
                title: translate("navigation.menu.initialFormulaTab"),
                description: translate("help.environment.formulaPanelInitial")
            };
        case resources.SETTINGS_ADDITIONAL_TAB_TYPE:
            return {
                title: translate("navigation.menu.additionalFormulaTab"),
                description: translate("help.environment.formulaPanel")
            };
        case resources.SETTINGS_OVERWRITE_LAST_TAB:
            return {
                title: translate("environmentsSettings.overwriteLastTab"),
                description: translate("help.environment.setOverwriteLastTab")
            };
        case resources.SETTINGS_DELETE_CONFIRMATION:
            return {
                title: translate("environmentsSettings.allowDeleteConfirmation"),
                description: translate("help.environment.deleteConfirmation")
            };
        case resources.SETTINGS_NAVIGATION_ITEMS_ORDERED:
            return {
                title: translate("navigation.menu.navigationItems"),
                description: translate("help.environment.setVisibilityAndOrder")
            };
        case resources.SUBMENU_QUINT_TINT:
            return {
                title: translate("general.moreInfo"),
                description: translate("help.environment.moreInfo")
            };
        case resources.SETTINGS_ROUNDING:
            return {
                title: translate("environmentsSettings.setDecimalPlaces"),
                description: translate("help.environment.setNumberOfDecimalPlaces")
            };
        case resources.SETTINGS_EDIT_COLORANT_ALLOWANCE:
            return {
                title: translate("formulaSettings.editColorantsAllowed"),
                description: translate("help.formulaSettings.allowEditingColorants")
            };
        case resources.SETTINGS_FORMULA_CODE:
            return {
                title: translate("general.colorCode"),
                description: translate("help.formulaSettings.useColorCode")
            };
        case resources.SETTINGS_FORMULA_NAME:
            return {
                title: translate("general.colorName"),
                description: translate("help.environment.setSearchPanel")
            };
        case resources.SETTINGS_FORMULA_AUTOMATIC_NAMING:
            return {
                title: translate("formulaSettings.allowFormulaAutomaticNaming"),
                description: translate("help.formulaSettings.formulaAutomaticNaming")
            };
        case resources.SETTINGS_FORMULA_PREVIEW:
            return {
                title: translate("formulaSettings.showColorPreview"),
                description: translate("help.formulaSettings.color")
            };
        case resources.SETTINGS_FORMULA_COLOR_PREVIEW:
            return {
                title: translate("formulaSettings.showColorPreview"),
                description: translate("help.formulaSettings.showColorPreview")
            };
        case resources.SETTINGS_INFO_ITEM_IN_RGB_PREVIEW:
            return {
                title: translate("environmentsSettings.setInfoItemInRgb"),
                description: translate("help.formulaSettings.setFormulaInfoInPreview")
            };
        case resources.SETTINGS_CUSTOM_FORMULAS:
            return {
                title: translate("formulaSettings.allowCustomFormulas"),
                description: translate("help.formulaSettings.allowCustomFormulas")
            };
        case resources.SETTINGS_CUSTOM_CAN_SIZE:
            return {
                title: translate("formulaSettings.allowCustomPackageSize"),
                description: translate("help.formulaSettings.allowCustomPackageSize")
            };
        case resources.SETTINGS_FORMULA_NOTES:
            return {
                title: translate("environmentsSettings.formulaNotes"),
                description: translate("help.environment.formulaNotes")
            };
        case resources.SETTINGS_DISPENSING_NOTES:
            return {
                title: translate("environmentsSettings.dispensingNotes"),
                description: translate("help.environment.dispensingNotes")
            };
        case resources.SETTINGS_CUSTOMERS_ENABLED:
            return {
                title: translate("environmentsSettings.allowCustomers"),
                description: translate("help.environment.allowCustomers")
            };
        case resources.SETTINGS_PRICE_TYPES:
            return {
                title: translate("navigation.menu.availablePrices"),
                description: translate("help.environment.allowPricing")
            };
        case resources.SETTINGS_PRICE_VISIBILITY_TYPES:
            return {
                title: translate("availablePrice.setPriceVatVisibility"),
                description: translate("help.priceSettings.VatPrice")
            };
        case resources.SETTINGS_NUMBER_OF_COMPETITIVE_COLORS:
            return {
                title: translate("formulaSettings.setNumberOfCompetitiveColors"),
                description: translate("help.environment.setNumberOfCompetitiveColors")
            };
        case resources.SETTINGS_MAX_TOLERANCE_FOR_COMPETITIVE_COLORS:
            return {
                title: translate("formulaSettings.setMaxToleranceForCompetitiveColors"),
                description: translate("help.environment.setMaxToleranceForCompetitiveColors")
            };
        case resources.SETTINGS_QUICK_TINT_SORTING_TYPE:
            return {
                title: translate("quickTintSettings.sorting"),
                description: translate("help.environment.sorting")
            };
        case resources.SETTINGS_QUICK_TINT_WINDOW_CLOSING_RULES:
            return {
                title: translate("navigation.menu.closing"),
                description: translate("help.environment.windowClosingRules")
            };
        case resources.SETTINGS_ORDER_MODAL_ENABLED:
            return {
                title: translate("environmentsSettings.allowOrderNotificationModal"),
                description: translate("help.environment.showNewOrderNotifications")
            };
        case resources.SETTINGS_SOUND_ENABLED:
            return {
                title: translate("environmentsSettings.allowSound"),
                description: translate("help.environment.allowSoundNotification")
            };
        case resources.SETTINGS_ORDER_SEARCH_ITEMS:
            return {
                title: translate("navigation.menu.orderSearch"),
                description: translate("help.environment.setOrderSearchParameters", {name: softwareNameLight})
            };
        case resources.SETTINGS_FTS:
            return {
                title: translate("environmentsSettings.setFts"),
                description: translate("help.environment.setFts")
            };
        case resources.SETTINGS_LOCALE:
            return {
                title: translate("navigation.menu.languageSettings"),
                description: translate("help.environment.setYourLanguage")
            };
        case resources.CONTENT_ITEM_TRANSLATIONS:
            return {
                title: translate("environmentsSettings.exportTranslationKeys"),
                description: translate("help.environment.exportTranslationKeys")
            };
        case resources.SETTINGS_KEYBOARD_SHORTCUTS:
            return {
                title: translate("navigation.menu.keyboardShortcuts"),
                description: translate("help.environment.setKeyboardShortcuts")
            };
        case resources.SETTINGS_HELP:
            return {
                title: translate("navigation.menu.helpItems"),
                description: translate("help.help")
            };
        case resources.CONTENT_ITEM_DEFAULT_PRICE_VALUES:
            return {
                title: translate("navigation.menu.defaultValues"),
                description: ""
            };
        case resources.CONTENT_ITEM_COLORANT_PRICING:
            return {
                title: translate("general.priceGroup"),
                description: translate("help.priceSettings.priceGroups")
            };
        case resources.CONTENT_ITEM_PRODUCT_PRICING:
            return {
                title: translate("pricing.productPricing"),
                description: translate("help.priceSettings.productPricing")
            };
        case resources.CONTENT_ITEM_PACKAGE_PRICING:
            return {
                title: translate("navigation.menu.packageSurcharge"),
                description: translate("help.priceSettings.packagePricing")
            };
        case resources.CONTENT_ITEM_PRICE_IMPORT:
            return {
                title: translate("pricing.import"),
                description: translate("help.priceSettings.import")
            };
        case resources.CONTENT_ITEM_PRICE_EXPORT:
            return {
                title: translate("pricing.export"),
                description: translate("help.priceSettings.export")
            };
        case resources.CONTENT_ITEM_CURRENCY_SETTINGS:
            return {
                title: translate("navigation.menu.currency"),
                description: translate("help.priceSettings.currencyNameNew")
            };
        case resources.CONTENT_ITEM_USER_MANAGEMENT:
            return {
                title: translate("navigation.menu.userManagemenet"),
                description: translate("help.userManagement.availableGroups")
            };
        case resources.SETTINGS_COMPETITIVE_COLOR_SEARCH:
            return {
                title: translate("formulaSettings.competitiveColorSearch"),
                description: translate("help.formulaSettings.setCompetitiveColorSearch")
            };
        case resources.SUBMENU_COLOR_REPRESENTATION:
            return {
                title: translate("formulaSettings.competitiveColorSearch"),
                description: translate("help.formulaSettings.setCompetitiveColorSearch")
            };
        case resources.SETTINGS_INFO_TYPES:
            return {
                title: translate("general.moreInfo"),
                description: translate("help.formulaSettings.setFormulaVisibilityAndOrder")
            };
        default:
            return {
                title: translate("general.settings"),
                description: ""
            };
    }
};

const getTranslationOfKeyword = (translationKey) => {
    switch (translationKey) {
        case keyWords.KEYWORD_ADVANCED:
            return translate("keyWords.advanced");
        case keyWords.KEYWORD_ALFA:
            return translate("keyWords.alfa");
        case keyWords.KEYWORD_CC3:
            return translate("keyWords.cC3");
        case keyWords.KEYWORD_CM:
            return translate("keyWords.cM");
        case keyWords.KEYWORD_CENTER:
            return translate("keyWords.center");
        case keyWords.KEYWORD_CENTRE:
            return translate("keyWords.centre");
        case keyWords.KEYWORD_COLORLINK3:
            return translate("keyWords.colorLink3");
        case keyWords.KEYWORD_COLORREADER:
            return translate("keyWords.colorReader");
        case keyWords.KEYWORD_COLORIX:
            return translate("keyWords.colorix");
        case keyWords.KEYWORD_CONTROL:
            return translate("keyWords.control");
        case keyWords.KEYWORD_COROB:
            return translate("keyWords.corob");
        case keyWords.KEYWORD_DATACOLOR:
            return translate("keyWords.datacolor");
        case keyWords.KEYWORD_DROMONT:
            return translate("keyWords.dromont");
        case keyWords.KEYWORD_DSPPROXY:
            return translate("keyWords.dspProxy");
        case keyWords.KEYWORD_EVODRIVER:
            return translate("keyWords.evoDriver");
        case keyWords.KEYWORD_EVOSERVICE:
            return translate("keyWords.evoService");
        case keyWords.KEYWORD_EYE_ONE:
            return translate("keyWords.eyeone");
        case keyWords.KEYWORD_EYEONE:
            return translate("keyWords.eyeOne");
        case keyWords.KEYWORD_F_F:
            return translate("keyWords.fF");
        case keyWords.KEYWORD_FAST_FLUID:
            return translate("keyWords.fastFluid");
        case keyWords.KEYWORD_FLEX:
            return translate("keyWords.flex");
        case keyWords.KEYWORD_HLD:
            return translate("keyWords.hLD");
        case keyWords.KEYWORD_HERO:
            return translate("keyWords.hero");
        case keyWords.KEYWORD_IDD:
            return translate("keyWords.iDD");
        case keyWords.KEYWORD_ITALTINTO:
            return translate("keyWords.italtinto");
        case keyWords.KEYWORD_LLD:
            return translate("keyWords.lLD");
        case keyWords.KEYWORD_LENTEQ:
            return translate("keyWords.lenteq");
        case keyWords.KEYWORD_LIGHT:
            return translate("keyWords.light");
        case keyWords.KEYWORD_LINUX:
            return translate("keyWords.linux");
        case keyWords.KEYWORD_MINOLTA:
            return translate("keyWords.minolta");
        case keyWords.KEYWORD_MISURA:
            return translate("keyWords.misura");
        case keyWords.KEYWORD_NANO:
            return translate("keyWords.nano");
        case keyWords.KEYWORD_NEXTDRIVER:
            return translate("keyWords.nextDriver");
        case keyWords.KEYWORD_NIX_PRO:
            return translate("keyWords.nixPro");
        case keyWords.KEYWORD_PCPDRIVER:
            return translate("keyWords.pCPDriver");
        case keyWords.KEYWORD_RM:
            return translate("keyWords.rM");
        case keyWords.KEYWORD_REDLIKE:
            return translate("keyWords.redlike");
        case keyWords.KEYWORD_SANTINT:
            return translate("keyWords.sanTint");
        case keyWords.KEYWORD_SILENT:
            return translate("keyWords.silent");
        case keyWords.KEYWORD_TMC:
            return "TMC";
        case keyWords.KEYWORD_U_S_GALLON:
            return translate("keyWords.uSGallon");
        case keyWords.KEYWORD_U_S_QUART:
            return translate("keyWords.uSQuart");
        case keyWords.KEYWORD_UIDD:
            return translate("keyWords.uIDD");
        case keyWords.KEYWORD_VAT:
            return translate("keyWords.vAT");
        case keyWords.KEYWORD_WINDOWS:
            return translate("keyWords.windows");
        case keyWords.KEYWORD_X_RITE:
            return translate("keyWords.xRite");
        case keyWords.KEYWORD_XRITE:
            return translate("keyWords.xrite");
        case keyWords.KEYWORD_ACTION:
            return translate("keyWords.action");
        case keyWords.KEYWORD_ADD:
            return translate("keyWords.add");
        case keyWords.KEYWORD_ADDITION:
            return translate("keyWords.addition");
        case keyWords.KEYWORD_ADDITIONAL:
            return translate("keyWords.additional");
        case keyWords.KEYWORD_ADDRESS:
            return translate("keyWords.address");
        case keyWords.KEYWORD_ADMIN:
            return translate("keyWords.admin");
        case keyWords.KEYWORD_AFTER:
            return translate("keyWords.after");
        case keyWords.KEYWORD_AFTER_DISPENSING:
            return translate("keyWords.afterDispensing");
        case keyWords.KEYWORD_ALL:
            return translate("keyWords.all");
        case keyWords.KEYWORD_ALLOW:
            return translate("keyWords.allow");
        case keyWords.KEYWORD_ALPHABETICALLY:
            return translate("keyWords.alphabetically");
        case keyWords.KEYWORD_AMOUNT:
            return translate("keyWords.amount");
        case keyWords.KEYWORD_API:
            return translate("keyWords.api");
        case keyWords.KEYWORD_ASK:
            return translate("keyWords.ask");
        case keyWords.KEYWORD_AUTOCAP:
            return translate("keyWords.autocap");
        case keyWords.KEYWORD_AUTOMATIC:
            return translate("keyWords.autotask");
        case keyWords.KEYWORD_AUTOTASK:
            return translate("keyWords.availablePrice");
        case keyWords.KEYWORD_AVAILABLE_PRICE:
            return translate("keyWords.availablePrice");
        case keyWords.KEYWORD_BACKUP:
            return translate("keyWords.backup");
        case keyWords.KEYWORD_BANNER:
            return translate("keyWords.banner");
        case keyWords.KEYWORD_BARCODE:
            return translate("keyWords.barcode");
        case keyWords.KEYWORD_BARCODES:
            return translate("keyWords.barcodes");
        case keyWords.KEYWORD_BASE:
            return translate("keyWords.base");
        case keyWords.KEYWORD_BEFORE:
            return translate("keyWords.before");
        case keyWords.KEYWORD_BEGIN:
            return translate("keyWords.begin");
        case keyWords.KEYWORD_BFS:
            return translate("keyWords.bfs");
        case keyWords.KEYWORD_BOTTOM:
            return translate("keyWords.bottom");
        case keyWords.KEYWORD_BRAND:
            return translate("keyWords.brand");
        case keyWords.KEYWORD_BRUSH:
            return translate("keyWords.brush");
        case keyWords.KEYWORD_BUG:
            return translate("keyWords.bug");
        case keyWords.KEYWORD_BUTTON:
            return translate("keyWords.button");
        case keyWords.KEYWORD_BUY:
            return translate("keyWords.buy");
        case keyWords.KEYWORD_CALIBRATION:
            return translate("keyWords.calibration");
        case keyWords.KEYWORD_CAN:
            return translate("keyWords.can");
        case keyWords.KEYWORD_CAN_HANDLING:
            return translate("keyWords.canHandling");
        case keyWords.KEYWORD_CANISTER:
            return translate("keyWords.canister");
        case keyWords.KEYWORD_CANISTER_FILLING:
            return translate("keyWords.canisterFilling");
        case keyWords.KEYWORD_CANISTER_LEVELS:
            return translate("keyWords.canisterLevels");
        case keyWords.KEYWORD_CANISTERS:
            return translate("keyWords.canisters");
        case keyWords.KEYWORD_CAP:
            return translate("keyWords.cap");
        case keyWords.KEYWORD_CHANGE:
            return translate("keyWords.change");
        case keyWords.KEYWORD_CHANGELOG:
            return translate("keyWords.changelog");
        case keyWords.KEYWORD_CHANNELS:
            return translate("keyWords.channels");
        case keyWords.KEYWORD_CHECK:
            return translate("keyWords.check");
        case keyWords.KEYWORD_CHUNK:
            return translate("keyWords.chunk");
        case keyWords.KEYWORD_CHUNKS:
            return translate("keyWords.chunks");
        case keyWords.KEYWORD_CITY:
            return translate("keyWords.city");
        case keyWords.KEYWORD_CLEAN:
            return translate("keyWords.clean");
        case keyWords.KEYWORD_CLEANING:
            return translate("keyWords.cleaning");
        case keyWords.KEYWORD_CLIENT_IDENTIFIER:
            return translate("keyWords.clientIdentifier");
        case keyWords.KEYWORD_CLOSE:
            return translate("keyWords.close");
        case keyWords.KEYWORD_CLOSEST:
            return translate("keyWords.closest");
        case keyWords.KEYWORD_CODE:
            return translate("keyWords.code");
        case keyWords.KEYWORD_COLLECT:
            return translate("keyWords.collect");
        case keyWords.KEYWORD_COLOR:
            return translate("keyWords.color");
        case keyWords.KEYWORD_COLOR_PANEL:
            return translate("keyWords.colorPanel");
        case keyWords.KEYWORD_COLORANT:
            return translate("keyWords.colorant");
        case keyWords.KEYWORD_COLORANT_CONSUMPTION:
            return translate("keyWords.colorantConsumption");
        case keyWords.KEYWORD_COLORANT_PRICE:
            return translate("keyWords.colorantPrice");
        case keyWords.KEYWORD_COLORANTS:
            return translate("keyWords.colorants");
        case keyWords.KEYWORD_COLOUR:
            return translate("keyWords.colour");
        case keyWords.KEYWORD_COMMUNICATION:
            return translate("keyWords.communication");
        case keyWords.KEYWORD_COMPARE:
            return translate("keyWords.compare");
        case keyWords.KEYWORD_COMPETITIVE:
            return translate("keyWords.competitive");
        case keyWords.KEYWORD_COMPUTER:
            return translate("keyWords.computer");
        case keyWords.KEYWORD_CONFIG:
            return translate("keyWords.config");
        case keyWords.KEYWORD_CONFIGURATION:
            return translate("keyWords.configuration");
        case keyWords.KEYWORD_CONFIRM:
            return translate("keyWords.confirm");
        case keyWords.KEYWORD_CONFIRMATION:
            return translate("keyWords.confirmation");
        case keyWords.KEYWORD_CONNECTION:
            return translate("keyWords.connection");
        case keyWords.KEYWORD_CONTACT_PERSON:
            return translate("keyWords.contactPerson");
        case keyWords.KEYWORD_COPIES:
            return translate("keyWords.copies");
        case keyWords.KEYWORD_COST:
            return translate("keyWords.cost");
        case keyWords.KEYWORD_COUNTRY:
            return translate("keyWords.country");
        case keyWords.KEYWORD_CREATE:
            return translate("keyWords.create");
        case keyWords.KEYWORD_CURRENCY:
            return translate("keyWords.currency");
        case keyWords.KEYWORD_CURRENT:
            return translate("keyWords.current");
        case keyWords.KEYWORD_CUSTOM:
            return translate("keyWords.custom");
        case keyWords.KEYWORD_CUSTOMERS:
            return translate("keyWords.customers");
        case keyWords.KEYWORD_DAILY:
            return translate("keyWords.daily");
        case keyWords.KEYWORD_DATA:
            return translate("keyWords.data");
        case keyWords.KEYWORD_DATABASE:
            return translate("keyWords.database");
        case keyWords.KEYWORD_DATE:
            return translate("keyWords.date");
        case keyWords.KEYWORD_DECIMALS:
            return translate("keyWords.decimals");
        case keyWords.KEYWORD_DEFAULT:
            return translate("keyWords.default");
        case keyWords.KEYWORD_DELAY:
            return translate("keyWords.delay");
        case keyWords.KEYWORD_DELETE:
            return translate("keyWords.delete");
        case keyWords.KEYWORD_DELETION:
            return translate("keyWords.deletion");
        case keyWords.KEYWORD_DELTA_E:
            return translate("keyWords.deltaE");
        case keyWords.KEYWORD_DEVICE:
            return translate("keyWords.device");
        case keyWords.KEYWORD_DEVICES:
            return translate("keyWords.devices");
        case keyWords.KEYWORD_DIAGNOSTIC_TILE:
            return translate("keyWords.diagnosticTile");
        case keyWords.KEYWORD_DIALOG:
            return translate("keyWords.dialog");
        case keyWords.KEYWORD_DIRECT:
            return translate("keyWords.direct");
        case keyWords.KEYWORD_DISCONNECT:
            return translate("keyWords.disconnect");
        case keyWords.KEYWORD_DISCOUNT:
            return translate("keyWords.discount");
        case keyWords.KEYWORD_DISPENSE:
            return translate("keyWords.dispense");
        case keyWords.KEYWORD_DISPENSER:
            return translate("keyWords.dispenser");
        case keyWords.KEYWORD_DISPENSING:
            return translate("keyWords.dispensing");
        case keyWords.KEYWORD_DOWN:
            return translate("keyWords.down");
        case keyWords.KEYWORD_DOWNLOAD:
            return translate("keyWords.download");
        case keyWords.KEYWORD_DRIVER:
            return translate("keyWords.driver");
        case keyWords.KEYWORD_EDIT:
            return translate("keyWords.edit");
        case keyWords.KEYWORD_EDITOR:
            return translate("keyWords.editor");
        case keyWords.KEYWORD_EMAIL:
            return translate("keyWords.email");
        case keyWords.KEYWORD_ERROR:
            return translate("keyWords.error");
        case keyWords.KEYWORD_EXCEL:
            return translate("keyWords.excel");
        case keyWords.KEYWORD_EXPIRATION:
            return translate("keyWords.expiration");
        case keyWords.KEYWORD_EXPORT:
            return translate("keyWords.export");
        case keyWords.KEYWORD_FANDECK:
            return translate("keyWords.fandeck");
        case keyWords.KEYWORD_FILE:
            return translate("keyWords.file");
        case keyWords.KEYWORD_FILL:
            return translate("keyWords.fill");
        case keyWords.KEYWORD_FILL_TO_MAX:
            return translate("keyWords.fillToMax");
        case keyWords.KEYWORD_FIND:
            return translate("keyWords.find");
        case keyWords.KEYWORD_FIRST:
            return translate("keyWords.first");
        case keyWords.KEYWORD_FOLDER:
            return translate("keyWords.folder");
        case keyWords.KEYWORD_FOMULA:
            return translate("keyWords.fomula");
        case keyWords.KEYWORD_FORMULA:
            return translate("keyWords.formula");
        case keyWords.KEYWORD_FORMULAS:
            return translate("keyWords.formulas");
        case keyWords.KEYWORD_FROM:
            return translate("keyWords.from");
        case keyWords.KEYWORD_FTS5:
            return translate("keyWords.fts5");
        case keyWords.KEYWORD_FULL_SCCREEN:
            return translate("keyWords.fullSccreen");
        case keyWords.KEYWORD_FULL_SCREEN:
            return translate("keyWords.fullScreen");
        case keyWords.KEYWORD_FULL_TEXT:
            return translate("keyWords.fullText");
        case keyWords.KEYWORD_FULLTEXT:
            return translate("keyWords.fulltext");
        case keyWords.KEYWORD_G:
            return translate("keyWords.g");
        case keyWords.KEYWORD_GENERIC_PRICING:
            return translate("keyWords.genericPricing");
        case keyWords.KEYWORD_HARD:
            return translate("keyWords.hard");
        case keyWords.KEYWORD_HBW:
            return translate("keyWords.hbw");
        case keyWords.KEYWORD_HELP:
            return translate("keyWords.help");
        case keyWords.KEYWORD_HELP_CENTER:
            return translate("keyWords.helpCenter");
        case keyWords.KEYWORD_HISTORY:
            return translate("keyWords.history");
        case keyWords.KEYWORD_HOME:
            return translate("keyWords.home");
        case keyWords.KEYWORD_HOST:
            return translate("keyWords.host");
        case keyWords.KEYWORD_HUMIDIFIER:
            return translate("keyWords.humidifier");
        case keyWords.KEYWORD_IDENTIFICATION:
            return translate("keyWords.identification");
        case keyWords.KEYWORD_IMAGE:
            return translate("keyWords.image");
        case keyWords.KEYWORD_IMP_GALLON:
            return translate("keyWords.impGallon");
        case keyWords.KEYWORD_IMP_QUART:
            return translate("keyWords.impQuart");
        case keyWords.KEYWORD_IMPORT:
            return translate("keyWords.import");
        case keyWords.KEYWORD_INDIVIDUALLY:
            return translate("keyWords.individually");
        case keyWords.KEYWORD_INFO:
            return translate("keyWords.info");
        case keyWords.KEYWORD_INFORMATION:
            return translate("keyWords.information");
        case keyWords.KEYWORD_INITIAL:
            return translate("keyWords.initial");
        case keyWords.KEYWORD_INITIALIZATION:
            return translate("keyWords.initialization");
        case keyWords.KEYWORD_INITIALIZE:
            return translate("keyWords.initialize");
        case keyWords.KEYWORD_INSERT:
            return translate("keyWords.insert");
        case keyWords.KEYWORD_INSTALL:
            return translate("keyWords.install");
        case keyWords.KEYWORD_INTELLY_PURGE:
            return translate("keyWords.intellyPurge");
        case keyWords.KEYWORD_INVALID:
            return translate("keyWords.invalid");
        case keyWords.KEYWORD_IP_ADDRESS:
            return translate("keyWords.ipAddress");
        case keyWords.KEYWORD_ITEMS:
            return translate("keyWords.items");
        case keyWords.KEYWORD_KEYBOARD:
            return translate("keyWords.keyboard");
        case keyWords.KEYWORD_KG:
            return translate("keyWords.kg");
        case keyWords.KEYWORD_KIOSK:
            return translate("keyWords.kiosk");
        case keyWords.KEYWORD_KIOSK_MODE:
            return translate("keyWords.kioskMode");
        case keyWords.KEYWORD_L:
            return translate("keyWords.l");
        case keyWords.KEYWORD_LAB:
            return translate("keyWords.lab");
        case keyWords.KEYWORD_LABEL:
            return translate("keyWords.label");
        case keyWords.KEYWORD_LABELS:
            return translate("keyWords.labels");
        case keyWords.KEYWORD_LANGUAGE:
            return translate("keyWords.language");
        case keyWords.KEYWORD_LARGO:
            return translate("keyWords.largo");
        case keyWords.KEYWORD_LAST:
            return translate("keyWords.last");
        case keyWords.KEYWORD_LEVELS:
            return translate("keyWords.levels");
        case keyWords.KEYWORD_LICENSE:
            return translate("keyWords.license");
        case keyWords.KEYWORD_LIMIT:
            return translate("keyWords.limit");
        case keyWords.KEYWORD_LINK:
            return translate("keyWords.link");
        case keyWords.KEYWORD_LOAD:
            return translate("keyWords.load");
        case keyWords.KEYWORD_LOCK:
            return translate("keyWords.lock");
        case keyWords.KEYWORD_LOG_FILES:
            return translate("keyWords.logFiles");
        case keyWords.KEYWORD_LOGIC:
            return translate("keyWords.logic");
        case keyWords.KEYWORD_LOGO:
            return translate("keyWords.logo");
        case keyWords.KEYWORD_LOWER:
            return translate("keyWords.lower");
        case keyWords.KEYWORD_MACHINE:
            return translate("keyWords.machine");
        case keyWords.KEYWORD_MACHINE_CONFIGURATION:
            return translate("keyWords.machineConfiguration");
        case keyWords.KEYWORD_MACHINE_MANAGEMENT:
            return translate("keyWords.machineManagement");
        case keyWords.KEYWORD_MACHINE_NAME:
            return translate("keyWords.machineName");
        case keyWords.KEYWORD_MAIN:
            return translate("keyWords.main");
        case keyWords.KEYWORD_MAINTENANCE:
            return translate("keyWords.maintenance");
        case keyWords.KEYWORD_MANU:
            return translate("keyWords.manu");
        case keyWords.KEYWORD_MANUAL:
            return translate("keyWords.manual");
        case keyWords.KEYWORD_MANUAL_ADDITION:
            return translate("keyWords.manualAddition");
        case keyWords.KEYWORD_MANUAL_DISPENSING:
            return translate("keyWords.manualDispensing");
        case keyWords.KEYWORD_MANUAL_SCAN:
            return translate("keyWords.manualScan");
        case keyWords.KEYWORD_MARGINS:
            return translate("keyWords.margins");
        case keyWords.KEYWORD_MARKUPS:
            return translate("keyWords.markups");
        case keyWords.KEYWORD_MASS:
            return translate("keyWords.mass");
        case keyWords.KEYWORD_MATCH:
            return translate("keyWords.match");
        case keyWords.KEYWORD_MEASURE:
            return translate("keyWords.measure");
        case keyWords.KEYWORD_MEASUREMENT:
            return translate("keyWords.measurement");
        case keyWords.KEYWORD_MEASUREMENT_UNITS:
            return translate("keyWords.measurementUnits");
        case keyWords.KEYWORD_MELODY:
            return translate("keyWords.melody");
        case keyWords.KEYWORD_MESSAGE:
            return translate("keyWords.message");
        case keyWords.KEYWORD_MINIMAL:
            return translate("keyWords.minimal");
        case keyWords.KEYWORD_MINUS:
            return translate("keyWords.minus");
        case keyWords.KEYWORD_MINUTES:
            return translate("keyWords.minutes");
        case keyWords.KEYWORD_ML:
            return translate("keyWords.ml");
        case keyWords.KEYWORD_MODE:
            return translate("keyWords.mode");
        case keyWords.KEYWORD_MODEL:
            return translate("keyWords.model");
        case keyWords.KEYWORD_MODIFICATION:
            return translate("keyWords.modification");
        case keyWords.KEYWORD_MODIFY:
            return translate("keyWords.modify");
        case keyWords.KEYWORD_MONTHLY:
            return translate("keyWords.monthly");
        case keyWords.KEYWORD_MORE_INFO:
            return translate("keyWords.moreInfo");
        case keyWords.KEYWORD_MOVABLE_TABLE:
            return translate("keyWords.movableTable");
        case keyWords.KEYWORD_MOVE:
            return translate("keyWords.move");
        case keyWords.KEYWORD_MUSIC:
            return translate("keyWords.music");
        case keyWords.KEYWORD_MY_ORDERS:
            return translate("keyWords.myOrders");
        case keyWords.KEYWORD_NAME:
            return translate("keyWords.name");
        case keyWords.KEYWORD_NAMING:
            return translate("keyWords.naming");
        case keyWords.KEYWORD_NAVIGATION:
            return translate("keyWords.navigation");
        case keyWords.KEYWORD_NEW:
            return translate("keyWords.new");
        case keyWords.KEYWORD_NIGHT:
            return translate("keyWords.night");
        case keyWords.KEYWORD_NOTES:
            return translate("keyWords.notes");
        case keyWords.KEYWORD_NOTIFICATION:
            return translate("keyWords.notification");
        case keyWords.KEYWORD_NUMBER:
            return translate("keyWords.number");
        case keyWords.KEYWORD_OFF:
            return translate("keyWords.off");
        case keyWords.KEYWORD_OPEN:
            return translate("keyWords.open");
        case keyWords.KEYWORD_ORDER:
            return translate("keyWords.order");
        case keyWords.KEYWORD_ORDERING:
            return translate("keyWords.ordering");
        case keyWords.KEYWORD_OVERFILL:
            return translate("keyWords.overfill");
        case keyWords.KEYWORD_OVERVIEW:
            return translate("keyWords.overview");
        case keyWords.KEYWORD_OVERWRITE:
            return translate("keyWords.overwrite");
        case keyWords.KEYWORD_OWN:
            return translate("keyWords.own");
        case keyWords.KEYWORD_PACKAGE:
            return translate("keyWords.package");
        case keyWords.KEYWORD_PACKAGE_PRICE:
            return translate("keyWords.packagePrice");
        case keyWords.KEYWORD_PACKAGES:
            return translate("keyWords.packages");
        case keyWords.KEYWORD_PANELS:
            return translate("keyWords.panels");
        case keyWords.KEYWORD_PARAMETER:
            return translate("keyWords.parameter");
        case keyWords.KEYWORD_PATH:
            return translate("keyWords.path");
        case keyWords.KEYWORD_PC:
            return translate("keyWords.pc");
        case keyWords.KEYWORD_PHONE:
            return translate("keyWords.phone");
        case keyWords.KEYWORD_PHONE_NUMBER:
            return translate("keyWords.phoneNumber");
        case keyWords.KEYWORD_PICTURE:
            return translate("keyWords.picture");
        case keyWords.KEYWORD_PLACE:
            return translate("keyWords.place");
        case keyWords.KEYWORD_PLACEMENT:
            return translate("keyWords.placement");
        case keyWords.KEYWORD_PLUG:
            return translate("keyWords.plug");
        case keyWords.KEYWORD_PLUGGER:
            return translate("keyWords.plugger");
        case keyWords.KEYWORD_PLUGGING:
            return translate("keyWords.plugging");
        case keyWords.KEYWORD_PLUGIN:
            return translate("keyWords.plugin");
        case keyWords.KEYWORD_PLUS:
            return translate("keyWords.plus");
        case keyWords.KEYWORD_POINT_OF_SALE:
            return translate("keyWords.pointOfSale");
        case keyWords.KEYWORD_POPUP:
            return translate("keyWords.popUp");
        case keyWords.KEYWORD_POS:
            return translate("keyWords.pos");
        case keyWords.KEYWORD_PRESENT:
            return translate("keyWords.present");
        case keyWords.KEYWORD_PREVIEW:
            return translate("keyWords.preview");
        case keyWords.KEYWORD_PRICE:
            return translate("keyWords.price");
        case keyWords.KEYWORD_PRICE_EXPORT:
            return translate("keyWords.priceExport");
        case keyWords.KEYWORD_PRICE_GROUP:
            return translate("keyWords.priceGroup");
        case keyWords.KEYWORD_PRICE_IMPORT:
            return translate("keyWords.priceImport");
        case keyWords.KEYWORD_PRICES:
            return translate("keyWords.prices");
        case keyWords.KEYWORD_PRICING:
            return translate("keyWords.pricing");
        case keyWords.KEYWORD_PRIMARY_CURRENCY:
            return translate("keyWords.primaryCurrency");
        case keyWords.KEYWORD_PRINT:
            return translate("keyWords.print");
        case keyWords.KEYWORD_PRINTER:
            return translate("keyWords.printer");
        case keyWords.KEYWORD_PRINTING:
            return translate("keyWords.printing");
        case keyWords.KEYWORD_PROBLEM:
            return translate("keyWords.problem");
        case keyWords.KEYWORD_PRODUCT:
            return translate("keyWords.product");
        case keyWords.KEYWORD_PRODUCT_PRICE:
            return translate("keyWords.productPrice");
        case keyWords.KEYWORD_PROFILED_VALUES:
            return translate("keyWords.profiledValues");
        case keyWords.KEYWORD_PUNCH:
            return translate("keyWords.punch");
        case keyWords.KEYWORD_PUNCHER:
            return translate("keyWords.puncher");
        case keyWords.KEYWORD_PUNCHING:
            return translate("keyWords.punching");
        case keyWords.KEYWORD_PURGE:
            return translate("keyWords.purge");
        case keyWords.KEYWORD_PURGE_TYPE:
            return translate("keyWords.purgeType");
        case keyWords.KEYWORD_PURGE_VOLUME:
            return translate("keyWords.purgeVolume");
        case keyWords.KEYWORD_QUALITY:
            return translate("keyWords.quality");
        case keyWords.KEYWORD_QUICK:
            return translate("keyWords.quick");
        case keyWords.KEYWORD_QUICK_TINT:
            return translate("keyWords.quickTint");
        case keyWords.KEYWORD_RAISE:
            return translate("keyWords.raise");
        case keyWords.KEYWORD_RCC:
            return translate("keyWords.rcc");
        case keyWords.KEYWORD_READER:
            return translate("keyWords.reader");
        case keyWords.KEYWORD_REBOOT:
            return translate("keyWords.reboot");
        case keyWords.KEYWORD_RECEIVE:
            return translate("keyWords.receive");
        case keyWords.KEYWORD_RECIRCULATE:
            return translate("keyWords.recirculate");
        case keyWords.KEYWORD_RECIRCULATION:
            return translate("keyWords.recirculation");
        case keyWords.KEYWORD_REFILL:
            return translate("keyWords.refill");
        case keyWords.KEYWORD_REGISTRATION:
            return translate("keyWords.registration");
        case keyWords.KEYWORD_REGULAR:
            return translate("keyWords.regular");
        case keyWords.KEYWORD_REMOVAL:
            return translate("keyWords.removal");
        case keyWords.KEYWORD_REMOVE:
            return translate("keyWords.remove");
        case keyWords.KEYWORD_REPORT:
            return translate("keyWords.report");
        case keyWords.KEYWORD_RESET:
            return translate("keyWords.reset");
        case keyWords.KEYWORD_RESOLUTION:
            return translate("keyWords.resolution");
        case keyWords.KEYWORD_RESTART:
            return translate("keyWords.restart");
        case keyWords.KEYWORD_RGB:
            return translate("keyWords.rgb");
        case keyWords.KEYWORD_RIGHTS:
            return translate("keyWords.rights");
        case keyWords.KEYWORD_ROUNDING:
            return translate("keyWords.rounding");
        case keyWords.KEYWORD_RULES:
            return translate("keyWords.rules");
        case keyWords.KEYWORD_SAVE:
            return translate("keyWords.save");
        case keyWords.KEYWORD_SCALE:
            return translate("keyWords.scale");
        case keyWords.KEYWORD_SCAN:
            return translate("keyWords.scan");
        case keyWords.KEYWORD_SCOPE:
            return translate("keyWords.scope");
        case keyWords.KEYWORD_SCREEN:
            return translate("keyWords.screen");
        case keyWords.KEYWORD_SCREENSAVER:
            return translate("keyWords.screensaver");
        case keyWords.KEYWORD_SEARCH:
            return translate("keyWords.search");
        case keyWords.KEYWORD_SECONDARY_CURRENCY:
            return translate("keyWords.secondaryCurrency");
        case keyWords.KEYWORD_SELECT:
            return translate("keyWords.select");
        case keyWords.KEYWORD_SELL:
            return translate("keyWords.sell");
        case keyWords.KEYWORD_SENSOR:
            return translate("keyWords.sensor");
        case keyWords.KEYWORD_SERIAL_NUMBER:
            return translate("keyWords.serialNumber");
        case keyWords.KEYWORD_SERVER:
            return translate("keyWords.server");
        case keyWords.KEYWORD_SERVERS:
            return translate("keyWords.servers");
        case keyWords.KEYWORD_SERVICE:
            return translate("keyWords.service");
        case keyWords.KEYWORD_SERVICE_ACTION:
            return translate("keyWords.serviceAction");
        case keyWords.KEYWORD_SERVICE_ACTIONS:
            return translate("keyWords.serviceActions");
        case keyWords.KEYWORD_SERVICE_SOFTWARE:
            return translate("keyWords.serviceSoftware");
        case keyWords.KEYWORD_SERVICE_WINDOW:
            return translate("keyWords.serviceWindow");
        case keyWords.KEYWORD_SETTINGS:
            return translate("keyWords.settings");
        case keyWords.KEYWORD_SHELF:
            return translate("keyWords.shelf");
        case keyWords.KEYWORD_SHORTCUTS:
            return translate("keyWords.shortcuts");
        case keyWords.KEYWORD_SHOT:
            return translate("keyWords.shot");
        case keyWords.KEYWORD_SHOW:
            return translate("keyWords.show");
        case keyWords.KEYWORD_SHUT_DOWN:
            return translate("keyWords.shutDown");
        case keyWords.KEYWORD_SHUTDOWN:
            return translate("keyWords.shutdown");
        case keyWords.KEYWORD_SIMPLE:
            return translate("keyWords.simple");
        case keyWords.KEYWORD_SIMULATION:
            return translate("keyWords.simulation");
        case keyWords.KEYWORD_SIZE:
            return translate("keyWords.size");
        case keyWords.KEYWORD_SKIP:
            return translate("keyWords.skip");
        case keyWords.KEYWORD_SMART_PURGE:
            return translate("keyWords.smartPurge");
        case keyWords.KEYWORD_SOFTWARE:
            return translate("keyWords.software");
        case keyWords.KEYWORD_SOFTWARE_SUPPLIER:
            return translate("keyWords.softwareSupplier");
        case keyWords.KEYWORD_SORT:
            return translate("keyWords.sort");
        case keyWords.KEYWORD_SOUND:
            return translate("keyWords.sound");
        case keyWords.KEYWORD_SPECTRO:
            return translate("keyWords.spectro");
        case keyWords.KEYWORD_SPECTROPHOTOMETER:
            return translate("keyWords.spectrophotometer");
        case keyWords.KEYWORD_SPEED:
            return translate("keyWords.speed");
        case keyWords.KEYWORD_SPLIT:
            return translate("keyWords.split");
        case keyWords.KEYWORD_START:
            return translate("keyWords.start");
        case keyWords.KEYWORD_STARTUP:
            return translate("keyWords.startup");
        case keyWords.KEYWORD_STATISTICS:
            return translate("keyWords.statistics");
        case keyWords.KEYWORD_STATUS:
            return translate("keyWords.status");
        case keyWords.KEYWORD_STIR:
            return translate("keyWords.stir");
        case keyWords.KEYWORD_STIRRING:
            return translate("keyWords.stirring");
        case keyWords.KEYWORD_STREET:
            return translate("keyWords.street");
        case keyWords.KEYWORD_SUPPORT:
            return translate("keyWords.support");
        case keyWords.KEYWORD_SURCHARGE:
            return translate("keyWords.surcharge");
        case keyWords.KEYWORD_TAB:
            return translate("keyWords.tab");
        case keyWords.KEYWORD_TABS:
            return translate("keyWords.tabs");
        case keyWords.KEYWORD_TASK:
            return translate("keyWords.task");
        case keyWords.KEYWORD_TEMPLATE:
            return translate("keyWords.template");
        case keyWords.KEYWORD_TESTING:
            return translate("keyWords.testing");
        case keyWords.KEYWORD_TIME:
            return translate("keyWords.time");
        case keyWords.KEYWORD_TINTING_MANAGEMENT_CENTER:
            return translate("keyWords.tintingManagementCenter");
        case keyWords.KEYWORD_TO:
            return translate("keyWords.to");
        case keyWords.KEYWORD_TO_DISPENSE:
            return translate("keyWords.toDispense");
        case keyWords.KEYWORD_TOLERANCE:
            return translate("keyWords.tolerance");
        case keyWords.KEYWORD_TOP:
            return translate("keyWords.top");
        case keyWords.KEYWORD_TOTAL:
            return translate("keyWords.total");
        case keyWords.KEYWORD_TRANSLATION:
            return translate("keyWords.translation");
        case keyWords.KEYWORD_TURN_OFF:
            return translate("keyWords.turnOff");
        case keyWords.KEYWORD_TURNTABLE:
            return translate("keyWords.turntable");
        case keyWords.KEYWORD_TYPE:
            return translate("keyWords.type");
        case keyWords.KEYWORD_UNIT:
            return translate("keyWords.unit");
        case keyWords.KEYWORD_UP:
            return translate("keyWords.up");
        case keyWords.KEYWORD_UPDATE:
            return translate("keyWords.update");
        case keyWords.KEYWORD_URL:
            return translate("keyWords.url");
        case keyWords.KEYWORD_USER:
            return translate("keyWords.user");
        case keyWords.KEYWORD_USER_GROUPS:
            return translate("keyWords.userGroups");
        case keyWords.KEYWORD_V1:
            return translate("keyWords.v1");
        case keyWords.KEYWORD_V2:
            return translate("keyWords.v2");
        case keyWords.KEYWORD_V3:
            return translate("keyWords.v3");
        case keyWords.KEYWORD_VALIDATE:
            return translate("keyWords.validate");
        case keyWords.KEYWORD_VALIDATION:
            return translate("keyWords.validation");
        case keyWords.KEYWORD_VERSION:
            return translate("keyWords.version");
        case keyWords.KEYWORD_VIRTUAL:
            return translate("keyWords.virtual");
        case keyWords.KEYWORD_VOLUME:
            return translate("keyWords.volume");
        case keyWords.KEYWORD_WEEKLY:
            return translate("keyWords.weekly");
        case keyWords.KEYWORD_WINDOW:
            return translate("keyWords.window");
        case keyWords.KEYWORD_WITH:
            return translate("keyWords.with");
        case keyWords.KEYWORD_WITHOUT:
            return translate("keyWords.without");
        case keyWords.KEYWORD_Y_VALUE:
            return translate("keyWords.yValue");
        case keyWords.KEYWORD_ZIP_CODE:
            return translate("keyWords.zipCode");
        case keyWords.KEYWORD_ZOOM:
            return translate("keyWords.zoom");
        default:
            return "";
    }
};

export const getSettingsKeyWordsTranslation = () => {
    let translatedKeyWords = {};

    let component = null;
    let keyWord = null;
    for (component in keyWordsData) {
        translatedKeyWords[component] = [];
        for (keyWord of keyWordsData[component]) {
            translatedKeyWords[component].push(getTranslationOfKeyword(keyWord));
        }
    }

    return translatedKeyWords;
};

export const getUnitTranslation = (unit) => {
    switch (unit) {
        case settings.UNIT_ML:
            return "ml";
        case settings.UNIT_L:
            return "L";
        case settings.UNIT_G:
            return "g";
        case settings.UNIT_KG:
            return "Kg";
        default:
            return "";
    }
};

export const getAutotaskHintTranslation = (action, softwareName) => {
    switch (action) {
        case autotaskTypes.ACTION_TYPE_PURGE_ALL:
            return translate("help.autotasks.purgeAll");
        case autotaskTypes.ACTION_TYPE_PURGE_FORMULA:
            return translate("help.autotasks.purgeChannels");
        case autotaskTypes.ACTION_TYPE_SMART_PURGE_ALL:
            return translate("help.autotasks.smartPurgeAll");
        case autotaskTypes.ACTION_TYPE_SMART_PURGE_CHANNELS:
            return translate("help.autotasks.smartPurgeChannels");
        case autotaskTypes.ACTION_TYPE_CLEAN_ALL:
            return translate("help.autotasks.cleanAll");
        case autotaskTypes.ACTION_TYPE_CLEAN_CHANNELS:
            return translate("help.autotasks.cleanChannels");
        case autotaskTypes.ACTION_TYPE_RECIRCULATE_ALL:
            return translate("help.autotasks.recirculateAll");
        case autotaskTypes.ACTION_TYPE_RECIRCULATE_CHANNELS:
            return translate("help.autotasks.recirculateChannels");
        case autotaskTypes.ACTION_TYPE_STIR_ALL:
            return translate("help.autotasks.stirAll");
        case autotaskTypes.ACTION_TYPE_STIR_CANISTERS:
            return translate("help.autotasks.stirCanisters");
        case autotaskTypes.ACTION_TYPE_BRUSH_MAINTENANCE:
            return translate("help.autotasks.brushMaintenance", {name: softwareName});
        case autotaskTypes.ACTION_TYPE_CAP_MAINTENANCE:
            return translate("help.autotasks.capMaintenance", {name: softwareName});
        case autotaskTypes.ACTION_TYPE_SWITCH_PLATE_MAINTENANCE:
            return translate("help.autotasks.switchPlateMaintenance", {name: softwareName});
        case autotaskTypes.ACTION_TYPE_DISPENSER_CLEANING:
            return translate("help.autotasks.dispenserCleaning", {name: softwareName});
        case autotaskTypes.ACTION_TYPE_HUMIDIFIER_WATER_LEVEL_MAINTENANCE:
            return translate("help.autotasks.humidifierWaterLevelMaintenance", {name: softwareName});
        case autotaskTypes.ACTION_TYPE_REFILL_ALL_MAINTENANCE:
            return translate("help.autotasks.refillAll", {name: softwareName});
        case autotaskTypes.ACTION_TYPE_REFILL_CANISTERS_MAINTENANCE:
            return translate("help.autotasks.refillCanisters", {name: softwareName});
        case autotaskTypes.ACTION_TYPE_COMPUTER_REBOOT:
            return translate("help.autotasks.computerReboot");
        case autotaskTypes.ACTION_TYPE_COMPUTER_SHUTDOWN:
            return translate("help.autotasks.computerShutdown");
        default:
            return "";
    }
};

export const getFormulaErrorTranslation = (error) => {
    switch (error) {
        case formulaErrors.TOTAL_COLORANT_AMOUNT_BELLOW_MIN_AMOUNT:
            return translate("help.formulaValidation.bellowBaseLimit");
        case formulaErrors.TOTAL_COLORANT_AMOUNT_OVER_MAX_AMOUNT:
            return translate("help.formulaValidation.overBaseLimit");
        case formulaErrors.COLORANT_AMOUNT_BELLOW_MIN_DOSABLE_AMOUNT:
            return translate("help.formulaValidation.colorantMinAmount");
        case formulaErrors.OVERFILL_DETECTED:
            return translate("help.formulaValidation.overfill");
        case formulaErrors.COLORANT_BASE_RULES_BROKEN:
            return translate("help.formulaValidation.colorantBaseRules");
        default:
            return "";
    }
};

export const getTicketTypeTranslation = (ticketType) => {
    switch (ticketType) {
        case ticketTypes.TICKET_TYPE_SERVICE_TICKET.id:
            return translate("ticketing.service");
        case ticketTypes.TICKET_TYPE_MESSAGE_TICKET.id:
            return translate("helpSettings.message");
        case ticketTypes.TICKET_TYPE_FORMULA_REQUEST.id:
            return translate("ticketing.formulaRequest");
        case ticketTypes.TICKET_TYPE_FORMULA_CLAIM.id:
            return translate("ticketing.formulaClaim");
        default:
            return "";
    }
};

export const getFormulaErrorTranslations = (errors) => {
    let errorText = "";

    for (let error of errors) {
        errorText += getFormulaErrorTranslation(error) + " ";
    }

    return errorText;
};

export const getFormulaBatchNumberErrorTranslation = (batchNumbers) => {
    if (batchNumbers.warning !== "") {
        return translate("help.formulaValidation.batchNumberWarning", {colorants: batchNumbers.warning});
    }
    if (batchNumbers.invalid !== "") {
        return translate("help.formulaValidation.batchNumberInvalid", {colorants: batchNumbers.invalid});
    }
    return "";
};

export const getSpectroModeTranslation = (mode) => {
    switch (mode) {
        case rcc.SPECTRO_MODE_45:
            return "45Â°";
        case rcc.SPECTRO_MODE_EGGSHELL:
            return translate("spectroMode.eggshell");
        case rcc.SPECTRO_MODE_FLAT:
            return translate("spectroMode.flat");
        case rcc.SPECTRO_MODE_GLOSS:
            return translate("spectroMode.gloss");
        case rcc.SPECTRO_MODE_MATT:
            return translate("spectroMode.matt");
        case rcc.SPECTRO_MODE_MATTE:
            return translate("spectroMode.matte");
        case rcc.SPECTRO_MODE_PEARL:
            return translate("spectroMode.pearl");
        case rcc.SPECTRO_MODE_SATIN:
            return translate("spectroMode.satin");
        case rcc.SPECTRO_MODE_SEMI_GLOSS:
            return translate("spectroMode.semiGloss");
        default:
            return mode;
    }
};

export const getProtocolTranslation = (protocol, softwareName) => {
    switch (protocol) {
        case settings.PROTOCOL_FOLDER:
            return translate("fileTransfer.protocol.folder");
        case settings.PROTOCOL_FTP:
            return translate("fileTransfer.protocol.ftp");
        case settings.PROTOCOL_FTPS:
            return translate("fileTransfer.protocol.ftps");
        case settings.PROTOCOL_REDLIKE_STORAGE:
            return translate("fileTransfer.protocol.redlikeStorage", {softwareName: softwareName});
        case settings.PROTOCOL_SFTP:
            return translate("fileTransfer.protocol.sftp");
        default:
            return protocol;
    }
};

export const getFileTransferFormatTranslation = (formatType, defaultFormat) => {
    switch (formatType) {
        case settings.DESO_CUSTOM_FORMULA_XML:
            return translate("general.customFormula");
        case settings.DESO_CUSTOM_FORMULA_SIMPLE_XML:
            return translate("fileTransfer.customFormulaSimple");
        case settings.DESO_CUSTOMER_XML:
            return translate("general.customer");
        case settings.DESO_HISTORY_XML:
            return translate("navigation.menu.dispensingHistory");
        case settings.DESO_ORDER_XML:
            return translate("mainTab.order");
        default:
            return defaultFormat;
    }
};

export const getProductFileSourceTranslations = (source) => {
    switch (source) {
        case settings.SOURCE_LOCAL:
            return translate("formulaSettings.productFilesLocal");
        case settings.SOURCE_ASK:
            return translate("formulaSettings.productFilesDialog");
        case settings.SOURCE_ONLINE:
            return translate("formulaSettings.productFilesOnline");
        default:
            return "";
    }
};

export const getImagesSourceTranslations = (source) => {
    switch (source) {
        case settings.SOURCE_LOCAL:
            return translate("formulaSettings.imagesLocal");
        case settings.SOURCE_ONLINE:
            return translate("formulaSettings.imagesOnline");
        default:
            return "";
    }
};

export const getFileTypeTranslations = (fileType) => {
    switch (fileType) {
        case fileTypes.INSTRUCTIONS:
            return translate("general.instructions");
        case fileTypes.SAFETY_SHEET:
            return translate("general.safetySheet");
        case fileTypes.TECHNICAL_SHEET:
            return translate("general.technicalSheet");
        default:
            return "";
    }
};

export const formatRefillType = (refillType) => {
    switch (refillType) {
        case rcc.RCC_REFILL:
            return translate("general.refill");
        case rcc.RCC_REFILL_MANUAL_CORRECTION:
            return translate("general.refillManualLevelCorrection");
        default:
            return translate("general.refill");
    }
};

export const getModuleTranslation = (code) => {
    switch (code) {
        case modules.MODULE_COLORINVENT_MATCHING:
            return "ColorInvent matching";
        case modules.MODULE_DATACOLOR_MATCHING:
            return "Datacolor matching";
        case modules.MODULE_PARTIAL_UPDATES:
            return translate("general.partialUpdates");
        case modules.MODULE_PRODUCT_GROUPS:
            return translate("module.productGroups");
        case modules.MODULE_PACKAGE_CHECK:
            return "Servind check package";
        case modules.MODULE_TICKETING:
            return translate("general.ticketing");
        default:
            if (code.match(modules.MODULE_MATCHING_REGEX) !== null) {
                return translate("general.matching");
            }

            return code;
    }
};

export const getHistoricalFormulasPositionTranslations = (code) => {
    switch (code) {
        case settings.POSITION_COLORANT_PANEL:
            return translate("formulaSettings.buttonInColorantPanel");
        case settings.POSITION_BELOW_MORE_INFO:
            return translate("formulaSettings.buttonBellowMoreInfo");
        default:
            return translate("formulaSettings.hideHistoricalFormulas");
    }
};

export const getAlternativeFormulasPositionTranslations = (code) => {
    switch (code) {
        case settings.POSITION_BELOW_MORE_INFO:
            return translate("formulaSettings.buttonBellowMoreInfo");
        default:
            return translate("formulaSettings.hideAlternativeFormulas");
    }
};

export const getFormulaPropertiesPositionTranslations = (code) => {
    switch (code) {
        case settings.POSITION_FORMULA_STATUS_PANEL:
            return translate("formulaSettings.showInFormulaStatusPanel");
        case settings.POSITION_FORMULA_MORE_INFO_PANEL:
            return translate("formulaSettings.showInMoreInfoPanel");
        default:
            return translate("formulaSettings.hideFormulaProperties");
    }
};

export const getMatchingErrorTranslations = (resultCode, params = []) => {
    switch (resultCode) {
        case resultCodes.MATCHING_RESULT_INVALID_PRODUCT:
            return translate("flashMessage.matching.invalidProduct", params);
        case resultCodes.MATCHING_RESULT_INVALID_BIP:
            return translate("flashMessage.matching.invalidBase");
        case resultCodes.MATCHING_RESULT_INVALID_BIPP:
            return translate("flashMessage.matching.invalidBipp");
        case resultCodes.MATCHING_RESULT_INVALID_COLOR_FILE:
            return translate("flashMessage.matching.invalidColorfile", params);
        case resultCodes.MATCHING_RESULT_INVALID_SPECTRAL_CURVE:
            return translate("flashMessage.matching.invalidSpectralCurve");
        case resultCodes.MATCHING_RESULT_NO_COLOR_FILES_FOR_PRODUCT:
            return translate("flashMessage.matching.noColorfiles", params);
        case resultCodes.MATCHING_RESULT_UNKNOWN_ERROR:
            return translate("flashMessage.matching.unknownError");
        case resultCodes.MATCHING_RESULT_INVALID_CNT_ID:
            return translate("flashMessage.matching.invalidColorantId", params);
        case resultCodes.MATCHING_RESULT_INVALID_CNT_NAME:
            return translate("flashMessage.matching.invalidColorantName", params);
        case resultCodes.MATCHING_RESULT_CORRECTION_NOT_POSSIBLE:
            return translate("flashMessage.matching.correctionNotPossible");
        case resultCodes.MATCHING_RESULT_INVALID_ILLUMINANT:
            return translate("flashMessage.matching.invalidIlluminant", params);
        case resultCodes.MATCHING_RESULT_REFORMULATION_NOT_POSSIBLE:
            return translate("flashMessage.matching.reformulationNotPossible");
        case resultCodes.MATCHING_RESULT_DATACOLOR_MATCHING_DISABLED:
            return translate("flashMessage.datacolorMatching.disabled");
        case resultCodes.MATCHING_RESULT_DATACOLOR_MATCHING_OFFLINE:
            return translate("flashMessage.datacolorMatching.couldNotConnect");
        case resultCodes.MATCHING_RESULT_DATACOLOR_INVALID_CREDENTIALS:
            return translate("flashMessage.datacolorMatching.invalidCredentials");
        case resultCodes.MATCHING_RESULT_DATACOLOR_NO_CANS_AVAILABLE:
            return translate("flashMessage.datacolorMatching.noCansAvailable");
        case resultCodes.MATCHING_RESULT_NOT_AUTHORIZED:
            return translate("flashMessage.matching.notAuthorized", params);
        case resultCodes.MATCHING_RESULT_FORBIDDEN:
            return translate("flashMessage.matching.forbidden", params);
        case resultCodes.MATCHING_RESULT_NOT_FOUND:
            return translate("flashMessage.matching.notFound", params);
        case resultCodes.MATCHING_RESULT_VALIDATION_ERROR_WITH_DESCRIPTION:
            return translate("flashMessage.matching.validationError", params);
        case resultCodes.MATCHING_RESULT_INTERNAL_SERVER_ERROR:
            return translate("flashMessage.matching.internalServerError");
        case resultCodes.MATCHING_RESULT_DATACOLOR_INTERNAL_ERROR:
            return translate("flashMessage.datacolorMatching.internalError", params);
        default:
            return translate("flashMessage.matching.unknownError");
    }
};

export const getServerDialogTranslations = (data) => {
    const msgTranslated = translate("serverDialog." + data.id, data.params);

    if (msgTranslated === "serverDialog." + data.id) {
        return data.text;
    }

    return msgTranslated;
};

export const getFormulaTypesTranslations = (type) => {
    switch (type) {
        case statsTypes.UNDEFINED:
            return translate("general.undefined");
        case statsTypes.MAIN_FORMULA:
            return translate("general.standardFormula");
        case statsTypes.LOCAL_FORMULA:
            return translate("general.customFormula");
        case statsTypes.MODIFIED_MAIN_FORMULA:
            return translate("general.modifiedStandardFormula");
        case statsTypes.MODIFIED_LOCAL_FORMULA:
            return translate("general.modifiedCustomFormula");
        case statsTypes.QUICK_TINT:
            return translate("general.quickTint");
        case statsTypes.MANUAL_CORRECTION:
            return translate("general.manualCorrection");
        case statsTypes.LOCAL_FORMULA_MATCHING:
            return translate("general.matchingFormula");
        case statsTypes.ORDER:
            return translate("general.order");
        case statsTypes.AUTOMATIC_CORRECTION:
            return translate("general.automaticCorrection");
        case statsTypes.MODIFIED_AUTOMATIC_CORRECTION:
            return translate("general.modifiedAutomaticCorrection");
        case statsTypes.FINISHED_CORRECTION:
            return translate("general.finishedCorrection");
        case statsTypes.REFORMULATION:
            return translate("general.reformulation");
        default:
            return translate("general.undefined");
    }
};

export const getFormulaColorantPropertiesTranslations = (type, propertyUniversal = false) => {
    switch (type) {
        case infoData.FORMULA_COLORANT_GROUP_ID_SOLVENT_BASED:
            return translate("formulaProperties.solvent");
        case infoData.FORMULA_COLORANT_GROUP_ID_UNIVERSAL:
            return translate("formulaProperties.universal");
        case infoData.FORMULA_COLORANT_GROUP_ID_UNIVERSAL_SOLVENT_BASED:
            return propertyUniversal
                ? translate("formulaProperties.universal")
                : translate("formulaProperties.solvent");
        case infoData.FORMULA_COLORANT_GROUP_ID_UNIVERSAL_WATER_BASED:
            return propertyUniversal ? translate("formulaProperties.universal") : translate("formulaProperties.water");
        case infoData.FORMULA_COLORANT_GROUP_ID_WATER_BASED:
            return translate("formulaProperties.water");
        case infoData.FORMULA_COLORANT_GROUP_ID_WATER_BASED_SOLVENT_BASED:
            return translate("formulaProperties.invalid");
        default:
            return "";
    }
};

export const getFormulaDestinationPropertiesTranslations = (type) => {
    switch (type) {
        case infoData.FORMULA_DESTINATION_ID_EXTERIOR:
            return translate("formulaProperties.exterior");
        case infoData.FORMULA_DESTINATION_ID_INTERIOR:
            return translate("formulaProperties.interior");
        default:
            return "";
    }
};

export const getQCServerResponseTranslation = (response) => {
    switch (response) {
        case settings.CIE_LAB_2000:
            return "CIE 2000";
        case settings.CIE_LAB_1976:
            return "CIE 1976";
        case settings.CMC:
            return "CMC";
        default:
            return "";
    }
};

export const getQCDescriptorTranslation = (response) => {
    switch (response) {
        case qualityControl.MORE_BLUE:
            return translate("qualityControl.moreBlue");
        case qualityControl.MORE_RED:
            return translate("qualityControl.moreRed");
        case qualityControl.MORE_YELLOW:
            return translate("qualityControl.moreYellow");
        case qualityControl.MORE_GREEN:
            return translate("qualityControl.moreGreen");
        case qualityControl.LIGHTER:
            return translate("qualityControl.lighter");
        case qualityControl.DARKER:
            return translate("qualityControl.darker");
        default:
            return "";
    }
};

export const getDispensingHistoryExportColumns = (settings, canReadBuildingSite) => {
    let columns = {};

    if (settings.tableFormulaName) {
        columns["color_name"] = translate("general.colorName");
    }

    if (settings.tableFormulaCode && settings.formulaCode) {
        columns["color_code"] = translate("general.colorCode");
    }

    if (settings.tableFandeck) {
        columns["fandeck"] = translate("general.fandeck");
    }

    if (settings.tableProduct) {
        columns["product"] = translate("general.product");
    }

    if (settings.tableBase) {
        columns["base"] = translate("general.base");
    }

    if (settings.tablePackage) {
        columns["package"] = translate("general.package");
    }

    if (settings.tableBipp) {
        columns["bipp"] = translate("general.baseInProductPackage");
    }

    if (settings.tableOperator) {
        columns["operator"] = translate("general.operator");
    }

    if (settings.tableBuildingSite && canReadBuildingSite) {
        columns["building_site"] = translate("general.buildingSite");
    }

    if (settings.tableQuantity) {
        columns["dispensed_package_count"] = translate("mainTab.dispensedPackages");
        columns["package_count"] = translate("general.numberOfPackages");
    }

    if (settings.tableCustomer) {
        columns["customer"] = translate("general.customer");
    }

    if (settings.tableMoreInfo) {
        columns["more_info"] = translate("general.note");
    }

    if (settings.tableMoreInfoInternal) {
        columns["more_info_internal"] = translate("general.internalNote");
    }

    if (settings.tableClientName) {
        columns["client_name"] = translate("clientServerSettings.clientIdentifier");
    }

    if (settings.tableDate) {
        columns["date"] = translate("general.date");
    }

    return columns;
};
